import BarraMenu from "../../../components/BarraMenu";
import PageHero from "../../../components/PageHero";
import useDigitalTheme from "../../hooks/useDigitalTheme";
import API_HOST from "../../../ApiConfig";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { httpApiPostText } from "../../../lib";
import { Alert, Button, Carousel, Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import { FaPencilAlt, FaRegHandPaper, FaInfoCircle, FaClipboardCheck, FaClock, FaCheckCircle, FaUsers } from "react-icons/fa";
import { MdAdd } from "react-icons/md";

import MsgDialog from "../../../components/MsgDialog";
import "./index.css";


/** Calificación en UI (coma) → formato decimal para API/MySQL (punto) */
const normalizeEvaluatedRangeForApi = (
  value: string | number | null | undefined
): string => {
  if (value === "" || value === null || value === undefined) return "";
  return String(value).trim().replace(",", ".");
};

/**
 * Resuelve calificación/feedback sin pisar valores ya guardados en memoria
 * con inputs vacíos del carrusel (solo el slide activo suele tener valor fiable en DOM).
 */
const resolveQuestionAnswer = (
  question: any,
  idx: number
): { evaluated_range: string; feedback: string } => {
  const nmSel = `quailify-${question.question_id}-${idx}`;
  const nmTxt = `qst-${question.question_id}-${idx}`;
  const elSel = document.getElementById(nmSel) as HTMLInputElement | null;
  const elTxt = document.getElementById(nmTxt) as HTMLTextAreaElement | HTMLInputElement | null;

  const domRange = (elSel?.value ?? "").trim();
  const storedRange = (question?.evaluated_range ?? "").toString().trim();
  const evaluated_range = domRange !== "" ? domRange : storedRange;

  const domFeedback = elTxt != null ? elTxt.value : null;
  const storedFeedback = (question?.feedback ?? "").toString();
  const feedback =
    domFeedback !== null && domFeedback !== ""
      ? domFeedback
      : storedFeedback;

  return { evaluated_range, feedback };
};

/** Parsea respuesta API (HTTP + body.code) */
const parseApiResult = (response: { code: number; message: string }) => {
  let body: any = null;
  try {
    body = JSON.parse(response.message);
  } catch {
    body = null;
  }
  const httpOk = response.code >= 200 && response.code < 300;
  const bodyOk = body == null || Number(body.code) === 1 || Number(body.code) === 3;
  return { httpOk, bodyOk, body, ok: httpOk && (body == null || Number(body.code) === 1) };
};

const RealizarEvaluacionesPage = () => {
  useDigitalTheme();
  let [data, setData] = useState(new Array());
  const emp: any = useSelector((state: any) => state.emp);

  // ── Paginación server-side ────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState("");
  const [searchActive, setSearchActive] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const PER_PAGE = 10;
  const SEARCH_MIN_CHARS = 3;
  const DEBOUNCE_MS = 400;
  // ─────────────────────────────────────────────────────────────────────────

  const [showRealizaEvalQuestion, setShowRealizaEvalQuestion] = useState(false);
  const [showEditaEvalQuestion, setShowEditaEvalQuestion] = useState(false);
  const [showEditaEval, setShowEditaEval] = useState(false);
  const [showRealizaEval, setShowRealizaEval] = useState(false);
  const [showMsgError, setShowMsgError] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [pregunta, setPregunta] = useState("");
  const [nombreUsr, setNombreUsr] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroApellido, setFiltroApellido] = useState("");
  const [filtroEstadoEval, setFiltroEstadoEval] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");
  const filtroClienteRef = useRef("");
  const filtroEstadoRef = useRef("");
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetHistoryId, setResetHistoryId] = useState<number | null>(null);
  const [filteredData, setFilteredData] = useState(data);
  let [idUsr, setIdUsr] = useState("");
  let [fecha, setFecha] = useState("");
  const [fechaVinculacion, setFechaVinculacion] = useState<string>("");
  // Debug states
  const [debugCode, setDebugCode] = useState<any>(null);
  const [debugMsg, setDebugMsg] = useState<string>("");
  const panelContext = useMemo(
    () => ({
      id_client: emp?.id_client || null,
      idbusiness_group: emp?.idbusiness_group || null,
    }),
    [emp?.id_client, emp?.idbusiness_group]
  );
  const isDigitalAnnualPanel = useMemo(
    () => Number(emp?.idbusiness_group) === 1 &&
          (emp?.id_client == null || Number(emp?.id_client) === 1),
    [emp?.idbusiness_group, emp?.id_client]
  );
  const getAnnualPeriodByDate = useCallback((_referenceDate?: string) => {
    // Siempre usar el año actual
    const year = new Date().getFullYear();
    return {
      year,
      start: `${year}-01-01`,
      end: `${year}-12-31`,
    };
  }, []);
  const getQuestionLimits = useCallback((question: any) => {
    const minimum = Number(String(question?.minimum ?? "").replace(",", "."));
    const maximum = Number(String(question?.maximum ?? "").replace(",", "."));

    if (Number.isFinite(minimum) && Number.isFinite(maximum) && maximum > minimum) {
      return { minimum, maximum };
    }

    if (isDigitalAnnualPanel) {
      return { minimum: 1, maximum: 5 };
    }

    return { minimum, maximum };
  }, [isDigitalAnnualPanel]);
  const isRateableQuestion = useCallback((question: any) => {
    const { minimum, maximum } = getQuestionLimits(question);
    return Number.isFinite(minimum) && Number.isFinite(maximum) && maximum > minimum;
  }, [getQuestionLimits]);
  const normalizePanelValue = useCallback((value: any) => {
    return String(value ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]/g, "")
      .trim();
  }, []);
  const hasValidDigitalAnnualQuestionSet = useCallback((questionList: any[]) => {
    if (!isDigitalAnnualPanel) return true;

    const activeQuestions = questionList.filter(
      (question: any) => String(question?.states ?? "activo").toLowerCase() !== "inactivo"
    );

    return activeQuestions.length >= 30;
  }, [isDigitalAnnualPanel]);

  let columns = [
    {
      name: "Id",
      selector: (row: any) => row.id_collaborator,
      sortable: true,
      width: "120px",
      omit: true,
    },
    {
      name: "Colaborador",
      selector: (row: any) => row.collaborator_name,
      sortable: true,
      width: "270px",
      cell: (row: any) => (
        <div style={{ fontWeight: "bold", color: "#2c3e50" }}>{row.collaborator_name}</div>
      ),
    },
    {
      name: "Cliente",
      selector: (row: any) => row.client_name,
      sortable: true,
      width: "160px",
      cell: (row: any) => (
        <div style={{ color: "#34495e" }}>{row.client_name}</div>
      ),
    },
    {
      name: "Vinculación",
      selector: (row: any) => row.user_registered ? row.user_registered.substring(0, 10) : "",
      sortable: true,
      width: "160px",
      cell: (row: any) => (
        <div
          style={{
            backgroundColor: "#e8f4fc",
            padding: "6px 10px",
            borderRadius: "4px",
            fontSize: "13px",
          }}
        >
          {row.user_registered ? row.user_registered.substring(0, 10) : ""}
        </div>
      ),
    },
    {
      name: "Fecha cierre",
      selector: (row: any) => row.evaluation_history_date_close ? row.evaluation_history_date_close.substring(0, 10) : "",
      sortable: true,
      width: "200px",
      cell: (row: any) => (
        <div
          style={{
            backgroundColor: row.evaluation_history_date_close ? "#e8fcf1" : "#f8f9fa",
            padding: "6px 10px",
            borderRadius: "4px",
            fontSize: "13px",
            color: row.evaluation_history_date_close ? "#0d6832" : "#6c757d",
          }}
        >
          {row.evaluation_history_date_close ? row.evaluation_history_date_close.substring(0, 10) : "Sin Evaluación"}
        </div>
      ),
    },
    {
      name: "Estado",
      selector: (row: any) => row.evaluation_history_status,
      sortable: true,
      width: "120px",
      cell: (row: any) => {
        let label = "";
        let color = "#6c757d";
        if (row.evaluation_history_status === 1 || row.evaluation_history_status === "1") {
          label = "Finalizada";
          color = "#28a745";
        } else if (row.evaluation_history_status === 0 || row.evaluation_history_status === "0") {
          label = "Abierta";
          color = "#ffc107";
        } else if (row.evaluation_history_status === null) {
          label = "No realizada";
          color = "#F28974";
        } else {
          label = "Sin estado";
        }
        return (
          <span style={{ fontWeight: "bold", color }}>{label}</span>
        );
      },
    },
    {
      name: "Valor Status",
      selector: (row: any) => row.evaluation_history_status,
      sortable: true,
      width: "120px",
      omit: true,
      cell: (row: any) => (
        <span style={{ fontWeight: "bold", color: "#007bff" }}>{row.evaluation_history_status !== undefined && row.evaluation_history_status !== null ? row.evaluation_history_status : "-"}</span>
      ),
    },
    {
      name: "Acciones",
      selector: (row: any) => row.Acciones,
      grow: 3,
      cell: (row: any) => {
        let label = "";
        let color = "";
        if (row.evaluation_history_status === 1 || row.evaluation_history_status === "1" || row.evaluation_history_status === null) {
          label = "Iniciar";
          color = "#007bff";
        } else if (row.evaluation_history_status === 0 || row.evaluation_history_status === "0") {
          label = "Continuar";
          color = "#28a745";
        } else {
          label = "Iniciar";
          color = "#007bff";
        }
        const handleClick = (e: React.MouseEvent) => {
          e.preventDefault(); // Prevenir cierre o recarga de la forma
          if (label === "Continuar") {
            editaEvaluacion(row.id_collaborator, row.collaborator_name, row.user_evaluation_date);
          } else {
            if (!row.id_collaborator) {
              setMsgError("No se pudo determinar el usuario a evaluar (id_collaborator nulo o indefinido). Por favor intente de nuevo.");
              setShowMsgError(true);
              return;
            }
            realizaEvaluacion(row.id_collaborator, row.collaborator_name, row.user_evaluation_date, row.user_registered);
            // No cerrar modal ni recargar nada aquí
          }
        };
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              style={{
                backgroundColor: color,
                color: "white",
                border: "none",
                borderRadius: 4,
                padding: "4px 12px",
                fontWeight: "bold",
                cursor: "pointer",
                minWidth: 100,
                fontSize: 14,
                whiteSpace: "nowrap"
              }}
              onClick={handleClick}
            >
              {label}
            </button>

          </div>
        );
      },
    },
  ];


  const pagOptions = {
    rowsPerPageText: "Filas por páginas",
    rangeSeparatorText: "de",
    selectAllRowsItem: true,
    selectAllRowsItemText: "Todos",
  };

  const simulateEscKeyPress = () => {
    const escEvent = new KeyboardEvent("keydown", {
      key: "Escape",
      keyCode: 27,
      code: "Escape",
      which: 27,
      bubbles: true,
    });
    document.dispatchEvent(escEvent);
  };

  const realizaEvaluacion = async (idUser: any, name: string, date: string, userRegistered?: string) => {
    setNombreUsr(name);

    setIdUsr(idUser);
    // Guardar fecha de vinculación para calcular períodos anuales
    if (userRegistered) {
      setFechaVinculacion(userRegistered.substring(0, 10));
    }
    setFecha(date);

    /*****************************
     *** Verifica si se puede hacer una Evaluación nueva
     *** (Obtener el evaluador desde el servidor para evitar datos locales obsoletos)
     ***/
    let assignedEvaluator = emp.id_user; // fallback al usuario logueado
    try {
      const userReq = { valid: "selectUser", id_user: `${idUser}` };
      const userResp = await httpApiPostText(API_HOST, "POST", { "Content-Type": "application/json" }, userReq);
      const userData = JSON.parse(userResp.message);
      if (userData && userData.payload && userData.payload.length > 0) {
        const serverEvaluator = userData.payload[0].id_evaluator;
        if (serverEvaluator && serverEvaluator !== "") assignedEvaluator = serverEvaluator;
      }
    } catch (err) {
      // Si falla la consulta, seguimos con el fallback (usuario en sesión)
      console.error('No se pudo obtener evaluador desde servidor, usando fallback:', err);
    }

    const req = {
      valid: "validStartEvaluation",
      id_collaborator: `${idUser}`,
      id_evaluator: `${assignedEvaluator}`,
      date: `${date ? date.substring(0, 10) : ""}`,
      ...panelContext,
    };
    const url = API_HOST;
    const response = await httpApiPostText(
      url,
      "POST",
      {
        "Content-Type": "application/json",
      },
      req
    );

    const arrDta = JSON.parse(response.message);

    // validaci´ón temporal
    console.log("Respuesta validStartEvaluation:", arrDta);

    if (arrDta.code === 1 || arrDta.code === 2) {
      setPregunta(`¿Desea iniciar la Evaluación para ${name}?`);
      setShowRealizaEvalQuestion(true);
    } else {
      setMsgError(
        "Para iniciar una nueva Evaluación, debe finalizar primero el proceso de retroalimentación pendiente."
      );
      setShowMsgError(true);
    }
  };

  const editaEvaluacion = async (idUser: any, name: string, date: string) => {
    setNombreUsr(name);
    setIdUsr(idUser);
    setFecha(date);

    /************************************************************
     *** Verifica si se puede hacer una edición de una Evaluación
     ************************************************************/
    // Obtener evaluador actual desde servidor para evitar usar datos locales obsoletos
    let assignedEvaluator2 = emp.id_user;
    try {
      const userReq2 = { valid: "selectUser", id_user: `${idUser}` };
      const userResp2 = await httpApiPostText(API_HOST, "POST", { "Content-Type": "application/json" }, userReq2);
      const userData2 = JSON.parse(userResp2.message);
      if (userData2 && userData2.payload && userData2.payload.length > 0) {
        const serverEvaluator2 = userData2.payload[0].id_evaluator;
        if (serverEvaluator2 && serverEvaluator2 !== "") assignedEvaluator2 = serverEvaluator2;
      }
    } catch (err) {
      console.error('No se pudo obtener evaluador desde servidor para edición, usando fallback:', err);
    }

    const req = {
      valid: "validStartEvaluation",
      id_collaborator: `${idUser}`,
      id_evaluator: `${assignedEvaluator2}`,
      date: `${date ? date.substring(0, 10) : ""}`, // No tocar esto por nada del mundo
      ...panelContext,
    };
    const url = API_HOST;
    const response = await httpApiPostText(
      url,
      "POST",
      {
        "Content-Type": "application/json",
      },
      req
    );

    const arrDta = JSON.parse(response.message);
    if (arrDta.code === 3) {
      setPregunta(`¿Desea continuar con la retroalimentación para ${name}?`);
      setShowEditaEvalQuestion(true);
    } else {
      setMsgError(
        "No se encontraron evaluaciones en curso para este colaborador. Puede iniciar una nueva Evaluación."
      );
      setShowMsgError(true);
    }
  };

  const formatoEvaluacion = () => {
    setShowRealizaEvalQuestion(false);
    setShowRealizaEval(true);
  };

  const formatoEdicionEvaluacion = () => {
    setShowEditaEvalQuestion(false);
    setShowEditaEval(true);
  };

  const [isLoading, setIsLoading] = useState(false);
  const resetEvaluation = async (id_history: number) => {
  try {
    const response = await httpApiPostText(
      API_HOST,
      "POST",
      { "Content-Type": "application/json" },
      { valid: "resetEvaluation", id_history: id_history }
    );
    const dta = JSON.parse(response.message);
    if (dta.code === 1) {
      setShowResetConfirm(false);
      preparaDatos(true, currentPage, searchActive, filtroCliente, filtroEstadoEval);
    } else {
      setMsgError("Error al reiniciar la evaluación");
      setShowMsgError(true);
    }
  } catch (error) {
    setMsgError("Error al reiniciar la evaluación");
    setShowMsgError(true);
  }
  };
    const preparaDatos = async (showLoading = true, page = 1, search = "", fClient = "", fStatus = "") => {
    try {
      if (showLoading) setIsLoading(true);

      const req: any = {
        valid: "getCollaboratorsEvaluatorPaged",
        id_evaluator: emp.id_user,
        id_client: emp.id_client || null,
        idbusiness_group: emp.idbusiness_group || null,
        page,
        per_page: PER_PAGE,
        filter_client: fClient,
        filter_status: fStatus,
      };

      // Solo enviar search si cumple mínimo de caracteres
      if (search.trim().length >= SEARCH_MIN_CHARS) {
        req.search = search.trim();
      }

      const url = API_HOST;
      const response = await httpApiPostText(url, "POST", { "Content-Type": "application/json" }, req);
      const dta = JSON.parse(response.message);

      if (dta.code === 1) {
        // El backend ya filtra por id_evaluator, id_client y idbusiness_group
        // No se hace doble filtro aquí para no romper la paginación server-side
        const payloadFiltrado = Array.isArray(dta.payload) ? dta.payload : [];

        const newData = new Array();
        payloadFiltrado.map((dta: any) => {
          const obj = {
            ...dta,
            Acciones: (
              <div className=" d-flex gap-3">
                <a
                  href="#!"
                  id={`${dta.id_collaborator}`}
                  onClick={() => {
                    realizaEvaluacion(dta.id_collaborator, dta.collaborator_name, dta.user_evaluation_date);
                  }}
                >
                  <MdAdd title="NUEVA EVALUACIÓN" color={"white"} className="bg-success rounded-circle p-1 h4" />
                </a>
                <a
                  href="#!"
                  id={`${dta.id_collaborator}`}
                  onClick={() => {
                    editaEvaluacion(dta.id_collaborator, dta.collaborator_name, dta.user_evaluation_date);
                  }}
                >
                  <FaPencilAlt title="REANUDAR EVALUACIÓN" color={"black"} className="bg-warning rounded-circle p-1 h4" />
                </a>
              </div>
            ),
          };
          newData.push(obj);
        });

        setData(newData);
        // Actualizar paginación desde respuesta del servidor
        setTotalRows(dta.total ?? newData.length);
        setCurrentPage(page);
      } else {
        setData([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error('[preparaDatos] Error al cargar datos:', error);
      setData([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (emp && emp.first_name && emp.first_name !== "") {
      setData([]);
      setFilteredData([]);
      setSearchInput("");
      setSearchActive("");
      setCurrentPage(1);
      setTotalRows(0);
      preparaDatos(true, 1, "");
    }
  }, [emp.first_name, emp.id_client, emp.idbusiness_group]);

  // Polling de fondo — respeta la búsqueda y página activa
  useEffect(() => {
    if (emp.first_name === "") return;

    const intervalId = setInterval(() => {
      const isAnyModalOpen =
        showRealizaEval ||
        showEditaEval ||
        showRealizaEvalQuestion ||
        showEditaEvalQuestion ||
        showMsgError ||
        showResetConfirm;

      if (!isAnyModalOpen) {
        preparaDatos(false, currentPage, searchActive, filtroClienteRef.current, filtroEstadoRef.current);
      }
    }, 15000);

    return () => clearInterval(intervalId);
  }, [
    emp.first_name,
    emp.id_client,
    emp.idbusiness_group,
    currentPage,
    searchActive,
    showRealizaEval,
    showEditaEval,
    showRealizaEvalQuestion,
    showEditaEvalQuestion,
    showMsgError,
    showResetConfirm
  ]);

  // ── Debounce para búsqueda server-side (mín. 3 chars, 400 ms) ─────────────
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchActive) return;
    if (trimmed.length > 0 && trimmed.length < SEARCH_MIN_CHARS) return;

    const timer = setTimeout(() => {
      setSearchActive(trimmed);
      preparaDatos(true, 1, trimmed, filtroCliente, filtroEstadoEval);
    }, trimmed.length === 0 ? 0 : DEBOUNCE_MS);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Manejador de paginación server-side
  const handlePageChange = (page: number) => {
    preparaDatos(true, page, searchActive, filtroCliente, filtroEstadoEval);
  };

  // Filtros server-side: al cambiar vuelve a página 1
  useEffect(() => {
    preparaDatos(true, 1, searchActive, filtroCliente, filtroEstadoEval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEstadoEval, filtroCliente]);

  useEffect(() => {
    setFilteredData([...data]);
  }, [data]);
  const cerrarModalRealiza = () => {
    setShowRealizaEval(false);
    preparaDatos(true, currentPage, searchActive, filtroCliente, filtroEstadoEval);
  };

  // Modal para Realiazr EVALUACIÓN nueva
  const ModalRealizaEvaluacion = (props: any) => {
    const nQuestionsF1ini = 1; // Constante para uso antes de cargar questions
    let [questions, setQuestions] = useState(new Array());
    const [comenzar, setComenzar] = useState(true);
    const [showFinalizalQuestion, setShowFinalizalQuestion] = useState(false);
    const [dateStart, setdateStart] = useState("");
    const [dateEnd, setDateEnd] = useState("");
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [localAnswers, setLocalAnswers] = useState<{
      [key: string]: { evaluated_range: string; feedback: string };
    }>({});
    const [evaluationId, setEvaluationId] = useState<string>("");
    const [isFase1Complete, setIsFase1Complete] = useState(false);
    const [isFase2Complete, setIsFase2Complete] = useState(false);
    const [fase1Saved, setFase1Saved] = useState(false); // Nueva: controla si Fase 1 ya fue guardada
    const [evaluationStatus, setEvaluationStatus] = useState<{
      fase1_completed: boolean;
      fase2_completed: boolean;
    } | null>(null);

    // 🔧 ESTADOS MOVIDOS DESDE COMPONENTE HIJO PARA EVITAR RE-MOUNT RESET
    const [index, setIndex] = useState(0);

    const fechaKey = fecha ? fecha.substring(0, 10) : "";
    const lastSavedQuestionKey = `realiza_eval_last_question_${props.idColaborador}_${emp.id_user}_${fechaKey}`;

    const getResumeIndexFromLocal = (questionsList: any[]) => {
      try {
        const raw = localStorage.getItem(lastSavedQuestionKey);
        if (!raw) return 0;
        const parsed = JSON.parse(raw);
        const savedQuestionId = parsed?.question_id ?? raw;
        if (!savedQuestionId) return 0;
        const idx = questionsList.findIndex(
          (q: any) => String(q?.question_id) === String(savedQuestionId)
        );
        return idx >= 0 ? idx : 0;
      } catch {
        return 0;
      }
    };

    const setResumeQuestionIdLocal = (questionId: any) => {
      if (questionId === undefined || questionId === null) return;
      try {
        localStorage.setItem(
          lastSavedQuestionKey,
          JSON.stringify({ question_id: String(questionId) })
        );
      } catch {
        // noop
      }
    };

    const clearResumeQuestionLocal = () => {
      try {
        localStorage.removeItem(lastSavedQuestionKey);
      } catch {
        // noop
      }
    };

    const nQuestionsF1 = useMemo(
      () => questions.filter((q: any) => q.evaluation_phase === 1).length,
      [questions]
    );
    const isPhase1 = index < (nQuestionsF1 || nQuestionsF1ini);

    let indiceArr: number = 0;

    // Función para actualizar feedback en el ModalRealizaEvaluacion
    const updateAnswerRealizaEval = (
      questionIndex: number,
      field: "evaluated_range" | "feedback",
      value: string
    ) => {

      // Actualizar directamente en el array de questions
      const updatedQuestions = [...questions];
      if (updatedQuestions[questionIndex]) {
        updatedQuestions[questionIndex][field] = value;
        setQuestions(updatedQuestions);

        // También guardar en localStorage para persistencia
        const questionId = questions[questionIndex]?.question_id;
        if (questionId) {
          const key = `realiza_eval_${props.idColaborador}_${questionId}`;
          const currentData = JSON.parse(localStorage.getItem(key) || "{}");
          currentData[field] = value;
          localStorage.setItem(key, JSON.stringify(currentData));
        }
      }
    };

    // useEffect para verificar completitud de fases cuando cambian index o questions
    useEffect(() => {
      if (questions.length > 0 && !comenzar) {
        setTimeout(() => checkFasesCompleteness(), 300);
      }
    }, [index, questions.length, comenzar]);

    // Función para calcular el período de evaluación anual basado en fecha de vinculación
    const calcularPeriodoAnual = (fechaVinculacion: string): { start: string; end: string; year: number } => {
      if (!fechaVinculacion) {
        const currentYear = new Date().getFullYear();
        return { start: `${currentYear}-01-01`, end: `${currentYear}-12-31`, year: currentYear };
      }

      const vincDate = new Date(fechaVinculacion);
      const vincMonth = vincDate.getMonth(); // 0-11
      const vincDay = vincDate.getDate();
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();

      // Calcular el año de evaluación correspondiente
      // Empezamos desde el año de vinculación y avanzamos hasta encontrar el período actual o pasado más cercano
      let evalYear = vincDate.getFullYear();
      
      // Si la fecha de vinculación es en el futuro respecto a hoy, usamos el año actual
      if (vincDate > currentDate) {
        evalYear = currentYear;
      } else {
        // Calcular cuántos años completos han pasado desde la vinculación
        let yearsPassed = currentYear - vincDate.getFullYear();
        
        // Si aún no hemos llegado al mes/día de aniversario de este año, restamos 1
        const currentMonth = currentDate.getMonth();
        const currentDay = currentDate.getDate();
        if (currentMonth < vincMonth || (currentMonth === vincMonth && currentDay < vincDay)) {
          yearsPassed--;
        }
        
        // El año de evaluación es el año de vinculación + años completos pasados
        // Esto nos da el período anual actual o el último completado
        evalYear = Math.min(vincDate.getFullYear() + yearsPassed, currentYear);
      }

      // Asegurar que no saltemos a años futuros
      evalYear = Math.min(evalYear, currentYear);

      // Construir las fechas de inicio y fin del período anual
      const startDate = new Date(evalYear, vincMonth, vincDay);
      const endDate = new Date(evalYear + 1, vincMonth, vincDay);

      const formatDate = (date: Date): string => {
        return date.toISOString().split('T')[0];
      };

      return {
        start: formatDate(startDate),
        end: formatDate(endDate),
        year: evalYear
      };
    };

    // useEffect para resetear las fechas cuando el modal se abre
    useEffect(() => {
      if (props.Show) {
        if (isDigitalAnnualPanel) {
          // Usar fecha de vinculación para calcular el período anual correcto
          const periodo = calcularPeriodoAnual(props.fechaVinculacion);
          setdateStart(periodo.start);
          setDateEnd(periodo.end);
          setSelectedYear(periodo.year);
        } else {
          setdateStart("");
          setDateEnd("");
        }
      }
    }, [props.Show, fecha, isDigitalAnnualPanel, selectedYear, props.fechaVinculacion]);

    // Consultar estado de la EVALUACIÓN desde la base de datos
    const checkEvaluationStatus = async () => {
      try {
        // Usar el evaluador asignado del colaborador (fallback al usuario logueado)
        const collaboratorForStatus = data.find((d: any) => Number(d.id_collaborator) === Number(props.idColaborador));
        const assignedEvaluatorForStatus = collaboratorForStatus?.id_evaluator ?? emp.id_user;
        const req = {
          valid: "getEvaluationStatus",
          id_collaborator: `${props.idColaborador}`,
          id_evaluator: `${assignedEvaluatorForStatus}`,
          date: `${fecha ? fecha.substring(0, 10) : ""}`,
          ...panelContext,
        };

        const url = API_HOST;
        const response = await httpApiPostText(
          url,
          "POST",
          {
            "Content-Type": "application/json",
          },
          req
        );

        const result = JSON.parse(response.message);
        if (result.code === 1 && result.status) {
          const status = result.status;
          setEvaluationStatus(status);

          // Configurar estados basados en el estado de la base de datos
          setFase1Saved(status.fase1_completed || false);

          return status;
        } else {
          // Si no hay estado, es una EVALUACIÓN nueva
          setEvaluationStatus({
            fase1_completed: false,
            fase2_completed: false,
          });
          setFase1Saved(false);
          return { fase1_completed: false, fase2_completed: false };
        }
      } catch (error) {
        console.error("Error checking evaluation status:", error);
        setEvaluationStatus({ fase1_completed: false, fase2_completed: false });
        setFase1Saved(false);
        return { fase1_completed: false, fase2_completed: false };
      }
    };

    const getPreguntas = async () => {
      // Usar el evaluador asignado del colaborador (fallback al usuario logueado)
      const collaboratorRecord = data.find((d: any) => Number(d.id_collaborator) === Number(props.idColaborador));
      const assignedEvaluator = collaboratorRecord?.id_evaluator ?? emp.id_user;
      
      console.log('[getPreguntas] Valores de periodo:', { dateStart, dateEnd });
      
      const req = {
        valid: "startEvaluationNew",
        id_collaborator: `${props.idColaborador}`,
        id_evaluator: `${assignedEvaluator}`,
        date: `${fecha ? fecha.substring(0, 10) : ""}`,
        period_start_date: dateStart || "",
        period_end_date: dateEnd || "",
        ...panelContext,
      };

      console.log('[getPreguntas] Request a enviar:', req);

      const url = API_HOST;
      const response = await httpApiPostText(
        url,
        "POST",
        {
          "Content-Type": "application/json",
        },
        req
      );

      const dta = JSON.parse(response.message);

      if (dta.code === 0) {
        setShowRealizaEval(false);
        setMsgError(dta.message);
        setShowMsgError(true);
      } else {
        questions = [];
        dta.payload.map((obj: any) => questions.push(obj));
        questions.sort(
          (objx: any, objy: any) => objx.evaluation_phase - objy.evaluation_phase
        );
        questions = questions.map((question: any) => {
          const { minimum, maximum } = getQuestionLimits(question);
          return { ...question, minimum, maximum };
        });

        if (!hasValidDigitalAnnualQuestionSet(questions)) {
          setQuestions([]);
          setShowRealizaEval(false);
          setComenzar(true);
          setMsgError(
            "En el panel de Digital solo deben aparecer evaluaciones anuales de 30 preguntas. Este formato no corresponde al anual."
          );
          setShowMsgError(true);
          return;
        }

        setQuestions([...questions]);

        const nQuestionsF1Local = questions.filter((q: any) => q.evaluation_phase === 1).length;
        setTimeout(() => checkFasesCompleteness(), 500);

        // Configurar evaluationId y localStorage
        if (questions.length > 0) {
          const evalId = `new_${props.idColaborador}_${emp.id_user
            }_${fecha ? fecha.substring(0, 10) : ""}`;
          setEvaluationId(evalId);

          // Detectar automáticamente si es EVALUACIÓN existente basándose en los datos
          const tieneRegistrosConId = questions.some((q) => q.id && q.id > 0);
          const tieneDatosDeDB = questions.some(
            (q) => q.evaluated_range !== null
          );

          if (tieneRegistrosConId || tieneDatosDeDB) {

            const storageKey = `evaluation_answers_${evalId}`;
            localStorage.removeItem(storageKey);

            // Detectar automáticamente el estado de las fases
            detectarEstadoFasesDesdeDB();
            setIndex(getResumeIndexFromLocal(questions));
          } else {
            const storageKey = `evaluation_answers_${evalId}`;
            const savedAnswers = localStorage.getItem(storageKey);
            if (savedAnswers) {
              const parsed = JSON.parse(savedAnswers);
              setLocalAnswers(parsed);
            }
            setIndex(getResumeIndexFromLocal(questions));
          }
        }
      }
    };

    // Detectar estado de las fases basándose en los datos cargados de la BD
    const detectarEstadoFasesDesdeDB = () => {

      if (questions.length === 0) return false;

      const nQuestionsF1 = questions.filter((q: any) => q.evaluation_phase === 1).length;

      // Analizar Fase 1 (preguntas fase 1)
      let fase1Completa = true;
      let preguntasAnalizadas = 0;
      let preguntasValidas = 0;

      for (let i = 0; i < Math.min(nQuestionsF1, questions.length); i++) {
        const pregunta = questions[i];
        const esPreguntaCalificable = pregunta.minimum < pregunta.maximum;


        if (esPreguntaCalificable) {
          preguntasAnalizadas++;
          const evaluatedRange =
            pregunta.evaluated_range?.toString().trim() || "";
          const calificacionNumerica = parseFloat(
            evaluatedRange.replace(",", ".")
          );
          const esCalificacionValida =
            evaluatedRange !== "" &&
            evaluatedRange !== null &&
            evaluatedRange !== "0" &&
            evaluatedRange !== "0.0" &&
            evaluatedRange !== "0.00" &&
            evaluatedRange !== "0,0" &&
            evaluatedRange !== "0,00" &&
            evaluatedRange !== "0.000000" &&
            !isNaN(calificacionNumerica) &&
            calificacionNumerica >= 1; // Cambio: usar >= 1 en lugar de > 0

          if (esCalificacionValida) {
            preguntasValidas++;
          } else {
            fase1Completa = false;
            break;
          }
        }
      }

      // Si Fase 1 está completa, marcarla como guardada Y limpiar preguntas de Fase 2
      if (fase1Completa && preguntasAnalizadas > 0) {


        // PREVENTIVO: Forzar null en preguntas (preguntas fase 1)+ para evitar datos corruptos
        for (let i = nQuestionsF1; i < questions.length; i++) {
          if (questions[i].evaluated_range !== null) {
            questions[i].evaluated_range = null;
            questions[i].feedback = null;
          }
        }

        setFase1Saved(true);
        setEvaluationStatus((prev) => ({
          fase1_completed: true,
          fase2_completed: prev?.fase2_completed || false,
        }));
      }

      // Analizar Fase 2 (todas las preguntas)
      let fase2Completa = true;
      for (let i = 0; i < questions.length; i++) {
        const pregunta = questions[i];
        const esPreguntaCalificable = pregunta.minimum < pregunta.maximum;

        if (esPreguntaCalificable) {
          const evaluatedRange =
            pregunta.evaluated_range?.toString().trim() || "";
          const calificacionNumerica = parseFloat(
            evaluatedRange.replace(",", ".")
          );
          const esCalificacionValida =
            evaluatedRange !== "" &&
            evaluatedRange !== null &&
            evaluatedRange !== "0" &&
            evaluatedRange !== "0.0" &&
            evaluatedRange !== "0.00" &&
            evaluatedRange !== "0,0" &&
            evaluatedRange !== "0,00" &&
            evaluatedRange !== "0.000000" &&
            !isNaN(calificacionNumerica) &&
            calificacionNumerica > 0;

          if (!esCalificacionValida) {
            fase2Completa = false;
            break;
          }
        }
      }

      // Si Fase 2 está completa, marcarla como guardada
      if (fase2Completa && fase1Completa) {
        setEvaluationStatus((prev) => ({
          fase1_completed: prev?.fase1_completed || true,
          fase2_completed: true,
        }));
      }

      return fase1Completa && preguntasAnalizadas > 0;
    };

    // Flag para evitar bucles infinitos en checkFasesCompleteness
    const [isCalculatingFases, setIsCalculatingFases] = useState(false);

    // Verificar completitud de las fases
    const checkFasesCompleteness = () => {
      // Evitar bucles infinitos
      if (isCalculatingFases) {
        return;
      }


      if (questions.length === 0) {
        setIsFase1Complete(false);
        setIsFase2Complete(false);
        return;
      }

      setIsCalculatingFases(true);

      const nQuestionsF1 = questions.filter((q: any) => q.evaluation_phase === 1).length;

      // Sincronizar sin pisar memoria con inputs vacíos del carrusel
      for (let i = 0; i < questions.length; i++) {
        const pregunta = questions[i];
        const { evaluated_range, feedback } = resolveQuestionAnswer(pregunta, i);
        pregunta.evaluated_range = evaluated_range;
        pregunta.feedback = feedback;
      }

      // Verificar Fase 1 (preguntas fase 1) - TODAS deben tener calificación >= 1
      let fase1Complete = true;
      let preguntasCalificablesF1 = 0;
      let preguntasCompletasF1 = 0;

      for (let i = 0; i < Math.min(nQuestionsF1, questions.length); i++) {
        const pregunta = questions[i];
        const esPreguntaCalificable = pregunta.minimum < pregunta.maximum;

        if (esPreguntaCalificable) {
          preguntasCalificablesF1++;
          const nmSel = `quailify-${pregunta.question_id}-${i}`;
          const valorDOM = getElement(nmSel) || "";
          const valorDB = pregunta.evaluated_range?.toString().trim() || "";
          const valorFinal = valorDOM || valorDB;

          const calificacionNumerica = parseFloat(valorFinal.replace(",", "."));
          const esCalificacionValida =
            valorFinal !== "" &&
            valorFinal !== "0" &&
            valorFinal !== "0.0" &&
            valorFinal !== "0.00" &&
            valorFinal !== "0,0" &&
            valorFinal !== "0,00" &&
            valorFinal !== "0.000000" &&
            !isNaN(calificacionNumerica) &&
            calificacionNumerica >= 1;

          if (esCalificacionValida) {
            preguntasCompletasF1++;
          } else {
            fase1Complete = false;
          }
        }
      }


      fase1Complete =
        preguntasCalificablesF1 === preguntasCompletasF1 &&
        preguntasCalificablesF1 > 0;
      setIsFase1Complete(fase1Complete);

      // Verificar Fase 2 - independiente de Fase 1
      let fase2Complete = false;
      if (nQuestionsF1 < questions.length) {
        // Hay preguntas de Fase 2, verificarlas de forma independiente
        fase2Complete = true;
        let preguntasCalificablesF2 = 0;
        for (let i = nQuestionsF1; i < questions.length; i++) {
          const pregunta = questions[i];
          const esPreguntaCalificable = pregunta.minimum < pregunta.maximum;
          if (esPreguntaCalificable) {
            preguntasCalificablesF2++;
            const domVal = getElement(`quailify-${pregunta.question_id}-${i}`);
            const valorFinal = domVal !== null ? domVal.trim() : (pregunta.evaluated_range?.toString().trim() || "");
            const calificacionNumerica = parseFloat(valorFinal.replace(",", "."));
            const esCalificacionValida =
              valorFinal !== "" && valorFinal !== "0" && valorFinal !== "0.0" &&
              valorFinal !== "0.00" && valorFinal !== "0,0" && valorFinal !== "0,00" &&
              valorFinal !== "0.000000" && !isNaN(calificacionNumerica) && calificacionNumerica >= 1;
            if (!esCalificacionValida) {
              fase2Complete = false;
              break;
            }
          }
        }
        if (preguntasCalificablesF2 === 0) fase2Complete = false;
      } else {
        // No hay preguntas de Fase 2, usar completitud de Fase 1
        fase2Complete = fase1Complete;
      }
      setIsFase2Complete(fase2Complete);

      // Limpiar la bandera al final
      setIsCalculatingFases(false);
    };

    // Actualizar respuesta localmente (sin guardar en backend)
    const updateAnswer = (
      questionIndex: number,
      field: "evaluated_range" | "feedback",
      value: string
    ) => {
      const questionId = questions[questionIndex].question_id;
      const key = `${questionId}_${questionIndex}`;

      const newAnswers = {
        ...localAnswers,
        [key]: {
          ...localAnswers[key],
          [field]: value,
        },
      };

      setLocalAnswers(newAnswers);

      // Guardar en localStorage
      if (evaluationId) {
        const storageKey = `evaluation_answers_${evaluationId}`;
        localStorage.setItem(storageKey, JSON.stringify(newAnswers));
      }
    };

    // Guardar Fase 1 completa en backend
    const saveFase1ToBackend = async () => {
      try {
        // Preparar datos de Fase 1 (preguntas fase 1)
        const fase1Answers = [];
        const nQuestionsF1 = questions.filter((q: any) => q.evaluation_phase === 1).length;

        for (let i = 0; i < Math.min(nQuestionsF1, questions.length); i++) {
          const question = questions[i];
          const { evaluated_range: evaluatedRange, feedback } = resolveQuestionAnswer(question, i);

          question.evaluated_range = evaluatedRange;
          question.feedback = feedback;

          const answerData = {
            id_log: question.id,
            question_id: question.question_id,
            user_id: question.user_id,
            evaluator_id: question.evaluator_id,
            evaluated_range: normalizeEvaluatedRangeForApi(evaluatedRange),
            feedback,
            make_evaluation_id: emp.id_user,
          };

          fase1Answers.push(answerData);
        }

        const req = {
          valid: "saveFase1Complete",
          answers: fase1Answers,
          evaluation_history_id: questions[0]?.evaluation_history_id || 0,
        };


        const url = API_HOST;
        const response = await httpApiPostText(
          url,
          "POST",
          {
            "Content-Type": "application/json",
          },
          req
        );

        const result = JSON.parse(response.message);

        if (result.code === 1) {
          // Fase 1 guardada exitosamente
          setFase1Saved(true); // Marcar que Fase 1 fue guardada
          return true;
        } else {
          setMsgError(result.message || "Error al guardar Fase 1");
          setShowMsgError(true);
          return false;
        }
      } catch (error) {
        setMsgError("Error al guardar Fase 1");
        setShowMsgError(true);
        return false;
      }
    };

    // Guardar Fase 2 completa en backend
    const saveFase2ToBackend = async () => {
      try {
        // Preparar datos de Fase 2 (preguntas 20+)
        const fase2Answers = [];
        const nQuestionsF1 = questions.filter((q: any) => q.evaluation_phase === 1).length;

        for (let i = nQuestionsF1; i < questions.length; i++) {
          const question = questions[i];
          const { evaluated_range: evaluatedRange, feedback } = resolveQuestionAnswer(question, i);

          question.evaluated_range = evaluatedRange;
          question.feedback = feedback;

          const answerData = {
            id_log: question.id,
            question_id: question.question_id,
            user_id: question.user_id,
            evaluator_id: question.evaluator_id,
            evaluated_range: normalizeEvaluatedRangeForApi(evaluatedRange),
            feedback,
            make_evaluation_id: emp.id_user,
          };

          fase2Answers.push(answerData);
        }

        const req = {
          valid: "saveFase2Complete",
          answers: fase2Answers,
          evaluation_history_id: questions[0]?.evaluation_history_id || 0,
          user_id: props.idColaborador,
          evaluator_id: emp.id_user,
          date: fecha ? fecha.substring(0, 10) : "",
        };

        const url = API_HOST;
        const response = await httpApiPostText(
          url,
          "POST",
          {
            "Content-Type": "application/json",
          },
          req
        );

        const result = JSON.parse(response.message);


        if (result.code === 1) {
          // Fase 2 guardada exitosamente - limpiar localStorage
          if (evaluationId) {
            localStorage.removeItem(`evaluation_answers_${evaluationId}`);
          }
          return true;
        } else {
          setMsgError(result.message || "Error al guardar Fase 2");
          setShowMsgError(true);
          return false;
        }
      } catch (error) {
        setMsgError("Error al guardar Fase 2");
        setShowMsgError(true);
        return false;
      }
    };

    const HandlerClickComenzar = () => {
      if (dateStart && dateEnd && dateStart > dateEnd) {
        setMsgError("El inicio del periodo no puede ser mayor que el fin del periodo.");
        setShowMsgError(true);
        return;
      }

      // Limpiar estados previos antes de cargar la evaluación
      setLocalAnswers({});
      setQuestions([]);
      setEvaluationId("");
      setIsFase1Complete(false);
      setIsFase2Complete(false);
      setFase1Saved(false);
      setEvaluationStatus({ fase1_completed: false, fase2_completed: false });
      clearResumeQuestionLocal();

      setComenzar(false);
      getPreguntas();
    };

    const OnClickGuardarSalir = async () => {

      // ===========================================Inicio código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================
      // Preparar datos para console.log
      const fase1Data = [];

      // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================
      const nQuestionsF1 = questions.filter((q: any) => Number(q.evaluation_phase) === 1).length;

      // Sincronizar la pregunta visible antes del guardado masivo
      if (questions[index]) {
        const current = resolveQuestionAnswer(questions[index], index);
        questions[index].evaluated_range = current.evaluated_range;
        questions[index].feedback = current.feedback;
      }

      // Guardar todas las preguntas de Fase 1 (preguntas fase 1)
      for (let i = 0; i < Math.min(nQuestionsF1, questions.length); i++) {
        const { evaluated_range, feedback } = resolveQuestionAnswer(questions[i], i);

        questions[i].evaluated_range = evaluated_range;
        questions[i].feedback = feedback;

        console.log(`Guardando Fase 1 - Pregunta ${i + 1} -> evaluated_range: "${questions[i].evaluated_range}", feedback: "${questions[i].feedback}"`);

        // Agregar al array de datos para el log
        fase1Data.push({
          pregunta: i + 1,
          question_id: questions[i].question_id,
          evaluated_range,
          feedback,
          user_id: questions[i].user_id,
          evaluator_id: questions[i].evaluator_id
        });
        // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================
        const questionSaved = await guardarPregunta(i);
        if (!questionSaved) {
          return;
        }
      }

      // ===========================================Inicio código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      // 🔍 LOG 1: Datos guardados de Fase 1
      console.log("=== FASE 1 COMPLETADA - DATOS GUARDADOS ===", {
        colaborador_id: props.idColaborador,
        evaluator_id: emp.id_user,
        fecha: fecha ? fecha.substring(0, 10) : "",
        total_preguntas_fase1: fase1Data.length,
        datos_fase1: fase1Data
      });

      // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      const fase1SavedOk = await saveFase1ToBackend();
      if (!fase1SavedOk) {
        return;
      }

      setEvaluationStatus((prev) => ({
        fase1_completed: true,
        fase2_completed: prev?.fase2_completed || false,
      }));

      cerrarModalRealiza();
    };

    const OnClickFinalizarSalirFase2 = async () => {
      // Guardar la pregunta actual antes de mostrar confirmación
      await guardarPregunta(index);
      // Verificar si todo está completo
      checkFasesCompleteness();
      // Mostrar confirmación de finalización total
      setShowFinalizalQuestion(true);
    };

    const guardarPregunta = async (idx: number) => {
      try {
        if (!questions[idx]) return false;

        const { evaluated_range, feedback } = resolveQuestionAnswer(questions[idx], idx);
        questions[idx].evaluated_range = evaluated_range;
        questions[idx].feedback = feedback;

        const req = {
          valid: "updateQuestion",
          id_log: questions[idx].id,
          question_id: questions[idx].question_id,
          user_id: questions[idx].user_id,
          evaluator_id: questions[idx].evaluator_id || emp.id_user,
          evaluated_range:
            evaluated_range === ""
              ? ""
              : normalizeEvaluatedRangeForApi(evaluated_range),
          feedback: feedback === "" ? "" : feedback,
          make_evaluation_id: emp.id_user,
        };

        const url = API_HOST;
        const response = await httpApiPostText(
          url,
          "PUT",
          {
            "Content-Type": "application/json",
          },
          req
        );

        const parsed = parseApiResult(response);
        if (!parsed.ok) {
          setMsgError(
            parsed.body?.message ||
              `No fue posible guardar la pregunta ${idx + 1}.`
          );
          setShowMsgError(true);
          return false;
        }

        return true;
      } catch (error) {
        setMsgError(`No fue posible guardar la pregunta ${idx + 1}.`);
        setShowMsgError(true);
        return false;
      }
    };

    /** Guardar progreso: mismo batch que Finalizar (sirve Federacion/Falabella/Bolivar/Digital) */
    const persistAnswersProgress = async () => {
      if (!questions?.length) return false;

      const currentIdx = Math.max(0, Math.min(index, questions.length - 1));
      if (questions[currentIdx]) {
        const cur = resolveQuestionAnswer(questions[currentIdx], currentIdx);
        questions[currentIdx].evaluated_range = cur.evaluated_range;
        questions[currentIdx].feedback = cur.feedback;
      }

      const answers: any[] = [];
      for (let i = 0; i < questions.length; i++) {
        const { evaluated_range, feedback } = resolveQuestionAnswer(questions[i], i);
        questions[i].evaluated_range = evaluated_range;
        questions[i].feedback = feedback;

        const hasData =
          String(evaluated_range || "").trim() !== "" ||
          String(feedback || "").trim() !== "";
        if (!hasData) continue;

        answers.push({
          id_log: questions[i].id,
          question_id: questions[i].question_id,
          user_id: questions[i].user_id,
          evaluator_id: questions[i].evaluator_id || emp.id_user,
          evaluated_range: normalizeEvaluatedRangeForApi(evaluated_range),
          feedback,
          make_evaluation_id: emp.id_user,
        });
      }

      if (!answers.length) return true;

      const historyId = questions[0]?.evaluation_history_id || 0;
      const req = {
        valid: "saveAnswersProgress",
        answers,
        evaluation_history_id: historyId,
      };

      const response = await httpApiPostText(
        API_HOST,
        "POST",
        { "Content-Type": "application/json" },
        req
      );
      const parsed = parseApiResult(response);

      // Fallback: si el endpoint nuevo no existe en un server viejo, guardar una a una
      if (!parsed.ok) {
        for (const a of answers) {
          const idx = questions.findIndex((q: any) => Number(q.id) === Number(a.id_log));
          if (idx < 0) continue;
          const ok = await guardarPregunta(idx);
          if (!ok) return false;
        }
        return true;
      }

      return true;
    };

    const OnClickGuardarProgreso = async () => {
      if (!questions?.length) return;
      const idxToSave = Math.max(0, Math.min(index, questions.length - 1));
      const ok = await persistAnswersProgress();
      if (!ok) {
        setMsgError("No fue posible guardar el progreso de la evaluación.");
        setShowMsgError(true);
        return;
      }
      setResumeQuestionIdLocal(questions[idxToSave]?.question_id);
      cerrarModalRealiza();
    };

    const finalizaEvaluacion = async () => {

      // ===========================================Inicio código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================
      // Preparar datos completos para console.log antes de finalizar
      const evaluacionCompleta = [];
      // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================  
      // Iterar sobre todas las preguntas y guardar cada una
      for (let i = 0; i < questions.length; i++) {
        const { evaluated_range, feedback } = resolveQuestionAnswer(questions[i], i);

        questions[i].evaluated_range = evaluated_range;
        questions[i].feedback = feedback;

        console.log(`Finalizando Fase 2 - Pregunta ${i + 1} -> evaluated_range: "${questions[i].evaluated_range}", feedback: "${questions[i].feedback}"`);
        
        // Asegurar que se guarde cada pregunta en la BD antes de cerrar
        const questionSaved = await guardarPregunta(i);
        if (!questionSaved) return;

        // ===========================================Inicio código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================
        const nQuestionsF1 = questions.filter((q: any) => Number(q.evaluation_phase) === 1).length;

        // Agregar al array completo para el log
        evaluacionCompleta.push({
          pregunta: i + 1,
          fase: i < nQuestionsF1 ? "Fase 1" : "Fase 2",
          question_id: questions[i].question_id,
          categoria: questions[i].categorie,
          titulo: questions[i].title,
          evaluated_range,
          feedback,
          minimum: questions[i].minimum,
          maximum: questions[i].maximum
        });

        // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

        // Actualizar en sistema local
        updateAnswer(i, "evaluated_range", evaluated_range || "");
        updateAnswer(i, "feedback", feedback || "");
      }

      // Envío del evaluador que estaba asignado al crear la evaluación (fallback al usuario actual)
      const collaboratorForFinish = data.find((d: any) => Number(d.id_collaborator) === Number(props.idColaborador));
      const assignedEvaluatorForFinish = collaboratorForFinish?.id_evaluator ?? emp.id_user;
      const req = {
        valid: "updateDateEvaluation",
        user_id: `${props.idColaborador}`,
        evaluator_id: `${assignedEvaluatorForFinish}`,
        date: `${fecha ? fecha.substring(0, 10) : ""}`,
      };
      // ===========================================Inicio código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      // 🔍 LOG 3: Datos finales enviados al servicio para guardar en BD
      console.log("=== FINALIZANDO EVALUACIÓN COMPLETA - DATOS ENVIADOS AL SERVICIO ===", {
        servicio_endpoint: "updateDateEvaluation",
        request_data: req,
        colaborador_id: props.idColaborador,
        evaluator_id: emp.id_user,
        fecha: fecha ? fecha.substring(0, 10) : "",
        total_preguntas: evaluacionCompleta.length,
        evaluacion_completa: evaluacionCompleta,
        resumen_fases: {
          fase1_preguntas: evaluacionCompleta.filter(p => p.fase === "Fase 1").length,
          fase2_preguntas: evaluacionCompleta.filter(p => p.fase === "Fase 2").length
        }
      });
      // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      const url = API_HOST;
      const response = await httpApiPostText(
        url,
        "PUT",
        {
          "Content-Type": "application/json",
        },
        req
      );

      setShowFinalizalQuestion(false);
      clearResumeQuestionLocal();
      cerrarModalRealiza();
    };

    const OnClickFinalizarSalir = async () => {
      setShowFinalizalQuestion(true);
    };

    const getElement = (id: string) => {
      const input = document.getElementById(id) as HTMLInputElement | null;

      if (input != null) {
        return input.value;
      }

      return null;
    };

    const setElement = (id: string, value: string) => {
      const input = document.getElementById(id) as HTMLInputElement | null;

      if (input != null) {
        input.value = value;
      }
    };

    // componente carrousell
    const ControlledCarousel2 = (props: any) => {

      // Estados y props
      const { index, setIndex } = props;
      const [showMsgErrorCalificacion, setShowMsgErrorCalificacion] = useState(false);
      let [forms, setForms] = useState({});

      // Unificada: guardar y navegar igual para ambas fases
      const carousellSelect = async (selectedIndex: number) => {
        setShowMsgErrorCalificacion(false);
        let idxArr = index;
        if (idxArr >= 0 && questions[idxArr]) {
          const { evaluated_range, feedback } = resolveQuestionAnswer(
            questions[idxArr],
            idxArr
          );

          questions[idxArr].evaluated_range = evaluated_range;
          questions[idxArr].feedback = feedback;
          updateAnswerRealizaEval(idxArr, "evaluated_range", evaluated_range);
          updateAnswerRealizaEval(idxArr, "feedback", feedback);
          const nmTxt = `qst-${props.preguntas[idxArr].question_id}-${idxArr}`;
          forms = { ...forms, [nmTxt]: feedback };
          setForms({ ...forms });
        }
        setIndex(selectedIndex);
      };

      // 💾  FUNCIÓN PARA GUARDAR EN BD (replicada de ControlledCarousel que funciona)
      const guardarPregunta = async (idx: number) => {
        const req = {
          valid: "updateQuestion",
          id_log: questions[idx].id,
          question_id: questions[idx].question_id, // >>> Manteniendo question_id por ahora
          user_id: questions[idx].user_id,
          evaluator_id: questions[idx].evaluator_id,
          evaluated_range:
            questions[idx].evaluated_range === "" ||
              questions[idx].evaluated_range === null
              ? ""
              : normalizeEvaluatedRangeForApi(questions[idx].evaluated_range),
          feedback:
            questions[idx].feedback === "" || questions[idx].feedback === null
              ? ""
              : questions[idx].feedback,
          make_evaluation_id: emp.id_user, // CAMPO FALTANTE AGREGADO
        };

        const url = API_HOST;
        const response = await httpApiPostText(
          url,
          "PUT",
          {
            "Content-Type": "application/json",
          },
          req
        );

      };

      const onSelectorBlur = (evnt: any) => {
        const val = parseFloat(evnt.target.value);
        const min = parseFloat(evnt.target.min);
        const max = parseFloat(evnt.target.max);

        if (evnt.target.value === "") {
          // no hacer nada, por ahora
          setShowMsgErrorCalificacion(false);
        } else if (val < min || val > max) {
          setShowMsgErrorCalificacion(true);
          const nmSlt = evnt.target.id;
          setElement(nmSlt, "");
        }
      };

      // >>> FUNCIÓN PARA RESTAURAR VALORES AL DOM (descomentada y arreglada)
      useEffect(() => {
        const updateQuestions = () => {
          if (props.preguntas.length > 0 && questions.length > 0) {

            // Restaurar valores desde el array questions actualizado
            questions.map((qst: any, idx: number) => {
              const nmSel = `quailify-${qst.question_id}-${idx}`;
              const nmTxt = `qst-${qst.question_id}-${idx}`;

              // Restaurar evaluated_range
              if (qst.evaluated_range !== null && qst.evaluated_range !== "") {
                const evr = qst.evaluated_range;
                setElement(nmSel, formatDisplayValue(evr));
              }

              // Restaurar feedback
              if (qst.feedback) {
                setElement(nmTxt, qst.feedback);
              }

              return null; // Para evitar warning de ESLint
            });
          }
        };

        updateQuestions();
      }, [questions.length]); // Solo cuando cambie el número de questions


      useEffect(() => {
        const applyIndicatorClasses = () => {
          const container = document.getElementById(
            `carousel-realiza-${props.idColaborador}`
          );
          if (!container) return false;
          const indicators = container.querySelectorAll(
            ".carousel-indicators [data-bs-slide-to], .carousel-indicators [data-bs-target]"
          );
          if (indicators.length === 0) return false;

          const isPhase1 = (q: any) => {
            const phaseRaw = String(q?.evaluation_phase ?? "")
              .trim()
              .toLowerCase();
            const phaseNum = Number(phaseRaw);
            if (q?.evaluation_phase === 1 || phaseNum === 1) return true;
            if (phaseRaw.includes("fase 1") || phaseRaw.includes("fase1"))
              return true;
            return false;
          };

          const isPhase2 = (q: any) => {
            const phaseRaw = String(q?.evaluation_phase ?? "")
              .trim()
              .toLowerCase();
            const phaseNum = Number(phaseRaw);
            if (q?.evaluation_phase === 2 || phaseNum === 2) return true;
            if (phaseRaw.includes("fase 2") || phaseRaw.includes("fase2"))
              return true;
            if (phaseRaw.includes("retroaliment")) return true;
            return false;
          };

          const firstPhase2Index = props.preguntas.findIndex((q: any) =>
            isPhase2(q)
          );
          let nQuestionsF1 = props.preguntas.filter((q: any) =>
            isPhase1(q)
          ).length;
          if (firstPhase2Index !== -1) {
            nQuestionsF1 = firstPhase2Index;
          }
          if (nQuestionsF1 <= 0 && props.preguntas.length > 0) {
            nQuestionsF1 = Math.floor(props.preguntas.length / 2);
          }
          const forceIndexSplit = nQuestionsF1 === 0;

          const getPhaseClass = (
            q: any,
            idx: number,
            fallbackF1Count: number
          ) => {
            if (forceIndexSplit) {
              return idx < fallbackF1Count ? "phase-1" : "phase-2";
            }
            if (isPhase2(q)) return "phase-2";
            if (isPhase1(q)) return "phase-1";
            return idx < fallbackF1Count ? "phase-1" : "phase-2";
          };

          indicators.forEach((indicator, idx) => {
            indicator.classList.remove("phase-1", "phase-2");
            const q = props.preguntas[idx];
            const phaseClass = getPhaseClass(q, idx, nQuestionsF1);
            indicator.classList.add(phaseClass);
          });
          return true;
        };

        if (props.preguntas.length === 0) {
          return;
        }

        const container = document.getElementById(
          `carousel-realiza-${props.idColaborador}`
        );
        if (!container) {
          return;
        }

        const observer = new MutationObserver(() => {
          applyIndicatorClasses();
        });

        observer.observe(container, {
          childList: true,
          subtree: true,
        });

        const timeoutId = setTimeout(() => {
          applyIndicatorClasses();
        }, 60);

        return () => {
          clearTimeout(timeoutId);
          observer.disconnect();
        };
      }, [props.preguntas, props.idColaborador]);

      // Número de preguntas de Fase 1
      const nQuestionsF1 = props.preguntas.filter((q: any) => {
        const phase = String(q.evaluation_phase ?? '').trim();
        return phase === '1' || phase === 'Fase 1' || phase === 'fase 1' || q.evaluation_phase === 1;
      }).length;

      return (
        <div className="eval-carousel-wrapper">
          <Carousel
            id={`carousel-realiza-${props.idColaborador}`}
            className="custom-carousel"
            activeIndex={index}
            onSelect={carousellSelect}
            interval={null}
            wrap={false}
            indicators={false}
            prevIcon={
              <span
                className="carousel-control-prev-icon"
                aria-hidden="true"
              ></span>
            }
            nextIcon={
              <span
                className="carousel-control-next-icon"
                aria-hidden="true"
              ></span>
            }
          >
            {props.preguntas.map((obj: any, idx: number) => {
              const phase = String(obj.evaluation_phase ?? '').trim();
              const isP1 = phase === '1' || phase === 'Fase 1' || phase === 'fase 1' || obj.evaluation_phase === 1;
              const phaseClass = isP1 ? 'phase-1' : 'phase-2';
              const phaseLabel = isP1 ? 'Fase 1' : 'Fase 2: Retroalimentación';
              return (
                <Carousel.Item key={obj.id}>
                  <div className={`eval-question-card ${phaseClass}`}>
                    <div className="eval-card-header">
                      <span className={`eval-phase-badge ${phaseClass}`}>{phaseLabel}</span>
                      <span className="eval-category">{obj.categorie}</span>
                    </div>
                    <h5 className="eval-title">{obj.title || obj.categorie}</h5>
                    {obj.description && obj.description !== obj.title && <p className="eval-description">{obj.description}</p>}
                    {isRateableQuestion(obj) ? (
                      <div className="eval-score-section">
                        <label className="eval-score-label">
                          Calificación <span className="eval-score-range">({obj.minimum} – {obj.maximum})</span>
                        </label>
                        <div className="numeric-input-container">
                          <input
                            type="text"
                            id={`quailify-${obj.question_id}-${idx}`}
                            placeholder=""
                            className="form-control p-2 numeric-input"
                            onKeyPress={(e) => {
                              const regex = /[0-9,\.]/;
                              if (!regex.test(e.key) && e.key !== "Backspace" && e.key !== "Tab") {
                                e.preventDefault();
                              }
                              if ((e.key === "," || e.key === ".") && (e.currentTarget.value.includes(",") || e.currentTarget.value.includes("."))) {
                                e.preventDefault();
                              }
                              if ((e.key === "," || e.key === ".") && e.currentTarget.value === "") {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              let newValue = e.target.value.replace(/\./g, ",");
                              const numericValue = parseFloat(newValue.replace(",", "."));
                              if (!isNaN(numericValue) && numericValue > obj.maximum) {
                                newValue = obj.maximum.toString().replace(".", ",");
                              }
                              e.target.value = newValue;
                              if (questions[idx]) {
                                questions[idx].evaluated_range = newValue;
                              }
                            }}
                              onBlur={(e) => {
                              onBlurLogic(e, obj, idx, questions, setShowMsgErrorCalificacion);
                              if (questions[idx] && e.target.value) {
                                questions[idx].evaluated_range = e.target.value;
                              }
                              setTimeout(() => checkFasesCompleteness(), 50);
                            }}
                            onFocus={() => setShowMsgErrorCalificacion(false)}
                          />
                          <div className="numeric-input-arrows">
                            <button
                              type="button"
                              className="arrow-button up"
                              onClick={() => {
                                const input = document.getElementById(`quailify-${obj.question_id}-${idx}`) as HTMLInputElement;
                                let value = input.value.replace(/\./g, ",");
                                let numValue = parseFloat(value.replace(",", ".") || "0");
                                if (value === "") numValue = obj.minimum;
                                numValue = Math.min(numValue + 0.1, obj.maximum);
                                input.value = numValue.toFixed(1).replace(".", ",");
                                if (questions[idx]) {
                                  questions[idx].evaluated_range = input.value;
                                }
                                setShowMsgErrorCalificacion(false);
                              }}
                              tabIndex={-1}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-up" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="arrow-button down"
                              onClick={() => {
                                const input = document.getElementById(`quailify-${obj.question_id}-${idx}`) as HTMLInputElement;
                                let value = input.value.replace(/\./g, ",");
                                let numValue = parseFloat(value.replace(",", ".") || "0");
                                if (value === "") numValue = obj.maximum;
                                numValue = Math.max(numValue - 0.1, obj.minimum);
                                input.value = numValue.toFixed(1).replace(".", ",");
                                if (questions[idx]) {
                                  questions[idx].evaluated_range = input.value;
                                }
                                setShowMsgErrorCalificacion(false);
                              }}
                              tabIndex={-1}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-down" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <Alert variant="danger" show={showMsgErrorCalificacion}>
                          <FaRegHandPaper /> La calificación debe estar entre {obj.minimum} y {obj.maximum}. Por favor, ingrese un valor válido.
                        </Alert>
                      </div>
                    ) : null}
                    <div className="eval-obs-section">
                      <label className="eval-obs-label">Observaciones</label>
                      <textarea
                        id={`qst-${obj.question_id}-${idx}`}
                        placeholder="Escriba sus observaciones aquí..."
                        className="eval-obs-textarea"
                        onBlur={(e) => {
                          if (questions[idx]) {
                            questions[idx].feedback = e.target.value;
                            const questionId = questions[idx]?.question_id;
                            if (questionId) {
                              const key = `edita_eval_${props.idColaborador}_${questionId}`;
                              const currentData = JSON.parse(localStorage.getItem(key) || "{}");
                              currentData.feedback = e.target.value;
                              localStorage.setItem(key, JSON.stringify(currentData));
                            }
                          }
                          setTimeout(() => checkFasesCompleteness(), 50);
                        }}
                      />
                    </div>
                  </div>
                </Carousel.Item>
              );
            })}
          </Carousel>
          {(() => {
            const navGroups: { label: string; indices: number[] }[] = [];
            props.preguntas.forEach((q: any, qIdx: number) => {
              const phase = String(q.evaluation_phase ?? '').trim();
              const isP1 = phase === '1' || phase === 'Fase 1' || phase === 'fase 1' || q.evaluation_phase === 1;
              const label = isP1 ? 'Fase 1' : 'Fase 2';
              let grp = navGroups.find(g => g.label === label);
              if (!grp) { grp = { label, indices: [] }; navGroups.push(grp); }
              grp.indices.push(qIdx);
            });
            return (
              <div className="eval-phase-nav">
                {navGroups.map((grp) => (
                  <div key={grp.label} className="eval-phase-group">
                    <span className={`eval-phase-group-label ${grp.label === 'Fase 1' ? 'phase-1-label' : 'phase-2-label'}`}>{grp.label}</span>
                    <div className="eval-nav-buttons">
                      {grp.indices.map((globalIdx, localIdx) => {
                        const q = props.preguntas[globalIdx];
                        const domVal = getElement(`quailify-${q.question_id}-${globalIdx}`);
                        const ratingVal = domVal !== null ? domVal.trim() : (q.evaluated_range?.toString() || '').trim();
                        const numericRating = parseFloat(ratingVal.replace(',', '.'));
                        const isAnswered = isRateableQuestion(q)
                          ? (ratingVal !== '' && !isNaN(numericRating) && numericRating >= 1)
                          : true;
                        const isCurrent = index === globalIdx;
                        const btnPhase = grp.label === 'Fase 1' ? 'phase-1-btn' : 'phase-2-btn';
                        return (
                          <button
                            key={globalIdx}
                            className={`eval-nav-btn ${btnPhase} ${isCurrent ? 'active' : isAnswered ? 'answered' : ''}`}
                            onClick={() => carousellSelect(globalIdx)}
                          >
                            {localIdx + 1}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      );
    };

    return (
      <>
        <Modal
          show={props.Show}
          centered={true}
          size="xl"
          onHide={props.handlerClickEnd}
          backdrop="static"
          keyboard={true}
        >
          <Modal.Header closeButton>
            <Modal.Title className="h3 text-center">
              Evaluación de: <span className="highlight">{props.name}</span>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            style={{
              height: "480px",
              overflow: "hidden",
            }}
          >
            <div className="w-100">
              {comenzar ? (
                <div
                  className="d-flex align-items-center justify-content-center w-100"
                  style={{ height: "440px" }}
                >
                  <div className="d-flex flex-column gap-3">
                    {isDigitalAnnualPanel ? (
                      <div>
                        <div className="form-group">
                          <label htmlFor="dateStart">
                            <strong>Período de Evaluación</strong>
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="dateStart"
                            value={dateStart}
                            onChange={(e) => setdateStart(e.target.value)}
                          />
                        </div>
                        <div className="form-group mt-2">
                          <label htmlFor="dateEnd">
                            <strong>Fin del período</strong>
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="dateEnd"
                            value={dateEnd}
                            onChange={(e) => setDateEnd(e.target.value)}
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="form-group">
                          <label htmlFor="dateStart">Inicio Periodo</label>
                          <input
                            type="date"
                            className="form-control"
                            id="dateStart"
                            value={dateStart}
                            onChange={(e) => setdateStart(e.target.value)}
                          />
                        </div>
                        <div className="form-group mt-2">
                          <label>Fin Periodo</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateEnd}
                            onChange={(e) => setDateEnd(e.target.value)}
                          />
                        </div>
                      </div>
                    )}
                    <div className="d-flex gap-3 justify-content-center">
                      <Button
                        type="button"
                        className="btn btn-success"
                        style={{ width: '100px', height: '40px' }}
                        onClick={HandlerClickComenzar}
                        disabled={!dateStart || !dateEnd || dateStart > dateEnd}
                      >
                        Comenzar
                      </Button>
                      <Button
                        type="button"
                        className="btn btn-secondary"
                        style={{ width: '100px', height: '40px' }}
                        onClick={props.HandlerClickOk}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="d-flex justify-content-center w-100"
                  style={{ paddingBottom: "50px" }}
                >
                  <ControlledCarousel2
                    idColaborador={props.idColaborador}
                    preguntas={questions}
                    index={index}
                    setIndex={setIndex}
                  />
                </div>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer className="">
            {!comenzar && (
              <>
                <div className="d-flex justify-content-center w-100 gap-2">
                  <Button
                    type="button"
                    className="btn btn-outline-secondary"
                    style={{ padding: "8px 24px", fontWeight: "bold" }}
                    onClick={OnClickGuardarProgreso}
                    disabled={!questions?.length}
                    title="Guardar la evaluación para continuar luego"
                  >
                    Guardar
                  </Button>
                  <Button
                    type="button"
                    className="btn"
                    style={{
                      backgroundColor: (fase1Saved ? isFase2Complete : isFase1Complete)
                        ? "#28a745"
                        : "#6c757d",
                      color: "white",
                      border: "none",
                      padding: "8px 24px",
                      fontWeight: "bold"
                    }}
                    onClick={
                      index === questions.length - 1 && isFase2Complete
                        ? OnClickFinalizarSalirFase2
                        : (fase1Saved ? OnClickFinalizarSalirFase2 : OnClickGuardarSalir)
                    }
                    disabled={!(fase1Saved ? isFase2Complete : isFase1Complete)}
                    title={
                      !(fase1Saved ? isFase2Complete : isFase1Complete)
                        ? (fase1Saved ? "Complete todas las calificaciones de Fase 2 para habilitar" : "Complete todas las calificaciones de Fase 1 para habilitar")
                        : ""
                    }
                  >
                    {(() => {
                      const isLastQuestion = index === questions.length - 1;
                      if (isLastQuestion && isFase2Complete) {
                        return "Finalizar Evaluación";
                      }
                      return fase1Saved ? "Finalizar Fase 2" : "Finalizar Fase 1";
                    })()}
                  </Button>
                </div>

                <div className="text-center mt-2 w-100">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    {(() => {
                      const isLastQuestion = index === questions.length - 1;
                      if (isLastQuestion && isFase2Complete) {
                        return 'Fase 1 y Fase 2 completadas - Use "Finalizar Evaluación" para terminar';
                      }
                      return !fase1Saved
                        ? 'Respuestas guardadas temporalmente - Use "Finalizar Fase 1" para guardar definitivamente'
                        : 'Fase 1 completada - Complete Fase 2 y use "Finalizar Fase 2" para terminar la Evaluación';
                    })()}
                  </small>
                </div>
              </>
            )}
          </Modal.Footer>
        </Modal>
        {/* Cuadro de dialogo de finalización de EVALUACIÓN*/}
        <MsgDialog
          Show={showFinalizalQuestion}
          Title="Finalizar Evaluación"
          Message={"Una vez finalizada no podrá modificar la información"}
          BtnOkName="Finalizar"
          BtnNokName="Cancelar"
          HanlerdClickNok={() => setShowFinalizalQuestion(false)}
          HandlerClickOk={finalizaEvaluacion}
        />
      </>
    );
  };

  const cerrarModal = () => {
    console.log('[MODAL-CLEANUP] Iniciando limpieza del modal de edición');

    // Limpiar referencias DOM
    const elementosCalificacion = document.querySelectorAll('[id^="quailify-"]');
    const elementosFeedback = document.querySelectorAll('[id^="qst-"]');

    console.log(`[MODAL-CLEANUP] Limpiando ${elementosCalificacion.length} elementos de calificación`);
    console.log(`[MODAL-CLEANUP] Limpiando ${elementosFeedback.length} elementos de feedback`);

    // Remover event listeners si existen
    elementosCalificacion.forEach(el => {
      if ((el as any)._listeners) {
        delete (el as any)._listeners;
      }
    });

    elementosFeedback.forEach(el => {
      if ((el as any)._listeners) {
        delete (el as any)._listeners;
      }
    });

    // Forzar limpieza después de un momento
    setTimeout(() => {
      console.log('[MODAL-CLEANUP] Modal limpiado completamente');
      if ((window as any).gc) {
        (window as any).gc();
      }
    }, 100);

    setShowEditaEval(false);
    preparaDatos(true, currentPage, searchActive, filtroCliente, filtroEstadoEval);
  };

  // Modal para editar EVALUACIÓN ya creada
  const ModalEditaEvaluacion = (props: any) => {
    const nQuestionsF1ini = 1; // Constante para uso antes de cargar questions
    let [questions, setQuestions] = useState(new Array());
    const [comenzar, setComenzar] = useState(true);
    const [showFinalizalQuestion, setShowFinalizalQuestion] = useState(false);
    const [isFase1Complete, setIsFase1Complete] = useState(false);
    const [isFase2Complete, setIsFase2Complete] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(nQuestionsF1ini);
    const [fase1Saved, setFase1Saved] = useState(false);
    const [isCalculatingFases, setIsCalculatingFases] = useState(false);
    let indiceArr: number = 0;

    const fechaKey = fecha ? fecha.substring(0, 10) : "";
    const lastSavedQuestionKey = `realiza_eval_last_question_${props.idColaborador}_${emp.id_user}_${fechaKey}`;

    const getResumeIndexFromLocal = (questionsList: any[]) => {
      try {
        const raw = localStorage.getItem(lastSavedQuestionKey);
        if (!raw) return -1;
        const parsed = JSON.parse(raw);
        const savedQuestionId = parsed?.question_id ?? raw;
        if (!savedQuestionId) return -1;
        const idx = questionsList.findIndex(
          (q: any) => String(q?.question_id) === String(savedQuestionId)
        );
        return idx >= 0 ? idx : -1;
      } catch {
        return -1;
      }
    };

    const setResumeQuestionIdLocal = (questionId: any) => {
      if (questionId === undefined || questionId === null) return;
      try {
        localStorage.setItem(
          lastSavedQuestionKey,
          JSON.stringify({ question_id: String(questionId) })
        );
      } catch {
        // noop
      }
    };

    const clearResumeQuestionLocal = () => {
      try {
        localStorage.removeItem(lastSavedQuestionKey);
      } catch {
        // noop
      }
    };

    const nQuestionsF1 = useMemo(
      () => questions.filter((q: any) => q.evaluation_phase === 1).length,
      [questions]
    );
    const isPhase1 = carouselIndex < (nQuestionsF1 || nQuestionsF1ini);

    const getPhase1CountForEdit = () => {
      if (questions.length === 0) return 0;
      const isPhase1 = (q: any) => {
        const phaseRaw = String(q?.evaluation_phase ?? "")
          .trim()
          .toLowerCase();
        const phaseNum = Number(phaseRaw);
        if (q?.evaluation_phase === 1 || phaseNum === 1) return true;
        if (phaseRaw.includes("fase 1") || phaseRaw.includes("fase1"))
          return true;
        return false;
      };
      const isPhase2 = (q: any) => {
        const phaseRaw = String(q?.evaluation_phase ?? "")
          .trim()
          .toLowerCase();
        const phaseNum = Number(phaseRaw);
        if (q?.evaluation_phase === 2 || phaseNum === 2) return true;
        if (phaseRaw.includes("fase 2") || phaseRaw.includes("fase2"))
          return true;
        if (phaseRaw.includes("retroaliment")) return true;
        return false;
      };

      const firstPhase2Index = questions.findIndex((q: any) => isPhase2(q));
      let phase1Count = questions.filter((q: any) => isPhase1(q)).length;

      if (firstPhase2Index !== -1) {
        phase1Count = firstPhase2Index;
      }
      if (phase1Count <= 0) {
        phase1Count = Math.floor(questions.length / 2);
      }
      return phase1Count;
    };


    // FUNCIÓN DE RESET COMPLETO DEL MODAL
    const resetModalCompleto = () => {
      console.log('[MODAL-RESET] 🔄 Iniciando reset completo del modal');

      // Reset de todos los estados del modal
      setQuestions([]);
      setComenzar(true);
      setShowFinalizalQuestion(false);
      setIsFase1Complete(false);
      setIsFase2Complete(false);
      setFase1Saved(false);
      setIsCalculatingFases(false);
      indiceArr = 0;

      // Limpiar cualquier elemento DOM que pueda haber quedado en caché
      setTimeout(() => {
        // Forzar limpieza de elementos de input que puedan estar cacheados
        const allInputs = document.querySelectorAll('input[id*="quailify-"], textarea[id*="qst-"]');
        allInputs.forEach((input: any) => {
          if (input && typeof input.value !== 'undefined') {
            input.value = '';
          }
        });

        // Trigger garbage collection si está disponible
        if (window.gc) {
          window.gc();
        }

        console.log('[MODAL-RESET] ✅ Reset completo finalizado');
      }, 100);
    };

    // useEffect para detectar cuando se abre/cierra el modal
    useEffect(() => {
      if (props.Show) {
        console.log('[MODAL-LIFECYCLE] 🚀 Modal se está abriendo - ejecutando reset');
        resetModalCompleto();
      } else {
        console.log('[MODAL-LIFECYCLE] 🔒 Modal se está cerrando');
      }
    }, [props.Show]);

    // Detectar estado cuando las preguntas cambien
    useEffect(() => {
      if (questions.length > 0) {
        detectarEstadoFasesDesdeDB();
      }
    }, [questions.length]);

    // Iniciar siempre en la primera pregunta de Fase 2 cuando se abre el modal de edición
    useEffect(() => {
      if (!comenzar && questions.length > 0) {
        const resumeIdx = getResumeIndexFromLocal(questions);
        const phase1Count = getPhase1CountForEdit();
        const targetIndex = resumeIdx >= 0 ? resumeIdx : Math.max(phase1Count, 0);
        if (carouselIndex !== targetIndex) {
          setCarouselIndex(targetIndex);
        }
      }
    }, [comenzar, questions.length]);

    // Detectar estado de las fases basándose en los datos cargados de la BD
    const detectarEstadoFasesDesdeDB = () => {
      if (questions.length === 0) return false;

      const nQuestionsF1 = questions.filter((q: any) => q.evaluation_phase === 1).length;

      // Analizar Fase 1 (preguntas fase 1)
      let fase1Completa = true;
      let preguntasAnalizadas = 0;
      let preguntasValidas = 0;

      for (let i = 0; i < Math.min(nQuestionsF1, questions.length); i++) {
        const pregunta = questions[i];
        const esPreguntaCalificable = pregunta.minimum < pregunta.maximum;

        if (esPreguntaCalificable) {
          preguntasAnalizadas++;
          const evaluatedRange =
            pregunta.evaluated_range?.toString().trim() || "";
          const calificacionNumerica = parseFloat(
            evaluatedRange.replace(",", ".")
          );
          const esCalificacionValida =
            evaluatedRange !== "" &&
            evaluatedRange !== null &&
            evaluatedRange !== "0" &&
            evaluatedRange !== "0.0" &&
            evaluatedRange !== "0.00" &&
            evaluatedRange !== "0,0" &&
            evaluatedRange !== "0,00" &&
            evaluatedRange !== "0.000000" &&
            !isNaN(calificacionNumerica) &&
            calificacionNumerica >= 1;

          if (esCalificacionValida) {
            preguntasValidas++;
          } else {
            fase1Completa = false;
            break;
          }
        }
      }

      // Si Fase 1 está completa, marcarla como guardada
      if (fase1Completa && preguntasAnalizadas > 0) {
        console.log(">>> [COMPONENTE-B] MARCANDO FASE 1 COMO GUARDADA");
        setFase1Saved(true);
      } else {
        console.log(
          ">>> [COMPONENTE-B] FASE 1 NO COMPLETA - NO SE MARCA COMO GUARDADA"
        );
      }

      return fase1Completa && preguntasAnalizadas > 0;
    };

    const checkFasesCompleteness = () => {
      // Evitar bucles infinitos
      if (isCalculatingFases) {
        return;
      }

      if (questions.length === 0) {
        setIsFase1Complete(false);
        setIsFase2Complete(false);
        return;
      }

      const nQuestionsF1 = questions.filter((q: any) => q.evaluation_phase === 1).length;

      setIsCalculatingFases(true);

      // Sincronizar sin pisar memoria con inputs vacíos del carrusel
      for (let i = 0; i < questions.length; i++) {
        const pregunta = questions[i];
        const { evaluated_range, feedback } = resolveQuestionAnswer(pregunta, i);
        pregunta.evaluated_range = evaluated_range;
        pregunta.feedback = feedback;
      }

      // Validar Fase 1 (preguntas fase 1)
      let fase1Completa = true;
      let preguntasAnalizadas = 0;
      let preguntasValidas = 0;

      for (let i = 0; i < Math.min(nQuestionsF1, questions.length); i++) {
        const pregunta = questions[i];
        const esPreguntaCalificable = pregunta.minimum < pregunta.maximum;

        if (esPreguntaCalificable) {
          preguntasAnalizadas++;
          const evaluatedRange =
            pregunta.evaluated_range?.toString().trim() || "";
          const calificacionNumerica = parseFloat(
            evaluatedRange.replace(",", ".")
          );
          const esCalificacionValida =
            evaluatedRange !== "" &&
            evaluatedRange !== null &&
            evaluatedRange !== "0" &&
            evaluatedRange !== "0.0" &&
            evaluatedRange !== "0.00" &&
            evaluatedRange !== "0,0" &&
            evaluatedRange !== "0,00" &&
            evaluatedRange !== "0.000000" &&
            !isNaN(calificacionNumerica) &&
            calificacionNumerica >= 1;

          if (esCalificacionValida) {
            preguntasValidas++;
          } else {
            fase1Completa = false;
          }
        }
      }


      const fase1EstaCompleta = fase1Completa && preguntasAnalizadas > 0;
      setIsFase1Complete(fase1EstaCompleta);

      // Validar Fase 2 - independiente de Fase 1
      let fase2Completa = false;
      if (nQuestionsF1 < questions.length) {
        // Hay preguntas de Fase 2, verificarlas de forma independiente
        fase2Completa = true;
        let preguntasCalificablesF2 = 0;
        for (let i = nQuestionsF1; i < questions.length; i++) {
          const pregunta = questions[i];
          const esPreguntaCalificable = pregunta.minimum < pregunta.maximum;
          if (esPreguntaCalificable) {
            preguntasCalificablesF2++;
            const domVal = getElement(`quailify-${pregunta.question_id}-${i}`);
            const valorFinal = domVal !== null ? domVal.trim() : (pregunta.evaluated_range?.toString().trim() || "");
            const calificacionNumerica = parseFloat(valorFinal.replace(",", "."));
            const esCalificacionValida =
              valorFinal !== "" && valorFinal !== "0" && valorFinal !== "0.0" &&
              valorFinal !== "0.00" && valorFinal !== "0,0" && valorFinal !== "0,00" &&
              valorFinal !== "0.000000" && !isNaN(calificacionNumerica) && calificacionNumerica >= 1;
            if (!esCalificacionValida) {
              fase2Completa = false;
            }
          }
        }
        if (preguntasCalificablesF2 === 0) fase2Completa = false;
      } else {
        // No hay preguntas de Fase 2, usar completitud de Fase 1
        fase2Completa = fase1EstaCompleta;
      }

      setIsFase2Complete(fase2Completa);

      // Limpiar la bandera al final
      setIsCalculatingFases(false);
    };

    // ✅ ÚNICO CAMBIO: getPreguntas ahora usa emp.id_user en lugar del evaluador asignado
    // para que cualquier evaluador pueda continuar una evaluación abierta por otro
    const getPreguntas = async () => {
      console.log('[MODAL-EDITA] 📋 Iniciando getPreguntas...');
      console.log(`[MODAL-EDITA] Parámetros: Colaborador=${props.idColaborador}, Evaluador=${emp.id_user}, Fecha=${fecha ? fecha.substring(0, 10) : ""}`);

      try {
        const req = {
          valid: "startEvaluation",
          id_collaborator: `${props.idColaborador}`,
          id_evaluator: `${emp.id_user}`, // ✅ siempre el evaluador actual — el backend ya no depende de esto para encontrar la evaluación abierta
          date: `${fecha ? fecha.substring(0, 10) : ""}`,
          ...panelContext,
        };

        const url = API_HOST;
        const response = await httpApiPostText(
          url,
          "POST",
          {
            "Content-Type": "application/json",
          },
          req
        );

        const dta = JSON.parse(response.message);

        if (!dta) {
          setShowEditaEval(false);
          setMsgError("Error al obtener los datos de la Evaluación");
          setShowMsgError(true);
          return;
        }

        // Evaluación ya finalizada
        if (dta.code === 1) {
          setShowEditaEval(false);
          setMsgError("Esta evaluación ya fue finalizada y no puede modificarse.");
          setShowMsgError(true);
          return;
        }

        // No encontrada, error, o payload vacío
        if (dta.code === 0 || !dta.payload || dta.payload.length === 0) {
          setShowEditaEval(false);
          setMsgError(dta?.message || "No se encontró una evaluación en curso para este colaborador.");
          setShowMsgError(true);
          return;
        }

        // ✅ code === 2: evaluación abierta encontrada con preguntas
        questions = [];
        dta.payload.map((obj: any) => questions.push(obj));
        questions.sort(
          (objx: any, objy: any) => objx.evaluation_phase - objy.evaluation_phase
        );
        questions = questions.map((question: any) => {
          const { minimum, maximum } = getQuestionLimits(question);
          return { ...question, minimum, maximum };
        });

        if (!hasValidDigitalAnnualQuestionSet(questions)) {
          setQuestions([]);
          setShowEditaEval(false);
          setMsgError(
            "En el panel de Digital solo deben aparecer evaluaciones anuales de 30 preguntas. Esta evaluación no corresponde al formato anual."
          );
          setShowMsgError(true);
          return;
        }

        setQuestions([...questions]);

        const nQuestionsF1Local = questions.filter((q: any) => q.evaluation_phase === 1).length;
        detectarEstadoFasesDesdeDB();
        setTimeout(() => checkFasesCompleteness(), 500);
        const resumeIdx = getResumeIndexFromLocal(questions);
        const initialIndex =
          resumeIdx >= 0 ? resumeIdx : Math.max(nQuestionsF1Local - 1, 0);
        setCarouselIndex(initialIndex);

        console.log(`[MODAL-EDITA] ✅ Preguntas cargadas exitosamente: ${questions.length} preguntas`);
        console.log(`[MODAL-EDITA] Primera pregunta:`, questions[0] ? {
          question_id: questions[0].question_id,
          description: questions[0].description?.substring(0, 50) + "...",
          evaluated_range: questions[0].evaluated_range,
          feedback: questions[0].feedback
        } : 'No hay preguntas');

        // Análisis de valores existentes
        const preguntasConCalificacion = questions.filter(q => q.evaluated_range && q.evaluated_range !== null && q.evaluated_range !== "" && q.evaluated_range !== "0" && q.evaluated_range !== "0.0");

        console.log(`[MODAL-EDITA] Preguntas con calificación existente: ${preguntasConCalificacion.length}/${questions.length}`);

        if (preguntasConCalificacion.length > 0) {
          console.log(`[MODAL-EDITA] Ejemplos de calificaciones existentes:`, preguntasConCalificacion.slice(0, 5).map(q => ({
            question_id: q.question_id,
            evaluated_range: q.evaluated_range,
            feedback: q.feedback
          })));
        }

        // CARGAR VALORES EXISTENTES EN LOS CAMPOS DOM (después de un pequeño delay)
        setTimeout(() => {
          cargarValoresEnCampos();
        }, 200);

      } catch (error) {
        setShowEditaEval(false);
        setMsgError("Error al procesar la solicitud");
        setShowMsgError(true);
        console.error(`[MODAL-EDITA] ❌ Error en getPreguntas:`, error);
      }
    };

    // Función para cargar valores existentes en los campos DOM
    const cargarValoresEnCampos = () => {
      console.log('[MODAL-EDITA] 🔄 Cargando valores existentes en campos DOM...');

      questions.forEach((question, idx) => {
        const calificacionId = `quailify-${question.question_id}-${idx}`;
        const observacionId = `qst-${question.question_id}-${idx}`;

        // Cargar calificación si existe CON FORMATEO AUTOMÁTICO
        if (question.evaluated_range && question.evaluated_range !== null && question.evaluated_range !== "" && question.evaluated_range !== "0") {
          const calificacionInput = document.getElementById(calificacionId) as HTMLInputElement;
          if (calificacionInput) {
            // APLICAR FORMATEO: convertir a decimal con 1 cifra y coma como separador
            const valorNumerico = parseFloat(question.evaluated_range.toString().replace(",", "."));
            const valorFormateado = isNaN(valorNumerico) ? question.evaluated_range : valorNumerico.toFixed(1).replace(".", ",");
            calificacionInput.value = valorFormateado;
            console.log(`[MODAL-EDITA] ✅ Calificación cargada y formateada para pregunta ${idx + 1}: ${question.evaluated_range} → ${valorFormateado}`);
          }
        }

        // Cargar observación si existe
        if (question.feedback && question.feedback !== null && question.feedback !== "") {
          const observacionInput = document.getElementById(observacionId) as HTMLInputElement;
          if (observacionInput) {
            observacionInput.value = question.feedback;
            console.log(`[MODAL-EDITA] ✅ Observación cargada para pregunta ${idx + 1}: "${observacionInput.value}"`);
          }
        }
      });

      console.log('[MODAL-EDITA] ✅ Carga de valores existentes completada');
    };

    const HandlerClickComenzar = () => {
      console.log('[MODAL-EDITA] 🎯 HandlerClickComenzar ejecutado - iniciando evaluación');
      console.log(`[MODAL-EDITA] Usuario: ${props.name} (ID: ${props.idColaborador})`);
      console.log(`[MODAL-EDITA] Estado actual - comenzar: ${comenzar}, questions.length: ${questions.length}`);

      setComenzar(false);
      getPreguntas();
    };

    const guardarPregunta = async (idx: number) => {
      console.log(`[MODAL-EDITA-DEBUG] === INICIANDO GUARDAR PREGUNTA ${idx + 1} ===`);

      if (!props.idColaborador) {
        console.error('[MODAL-ERROR] ID Colaborador no válido');
        return false;
      }

      if (!emp.id_user) {
        console.error('[MODAL-ERROR] ID Evaluador no válido');
        return false;
      }

      if (!fecha) {
        console.warn('[MODAL-WARN] Fecha vacía; se continúa con guardado de progreso');
      }

      if (!questions[idx]) {
        console.error(`[MODAL-ERROR] Pregunta en índice ${idx} no existe`);
        return false;
      }

      // Preferir memoria si el input del carrusel no existe o está vacío
      const { evaluated_range: calificacionValue, feedback: feedbackValue } =
        resolveQuestionAnswer(questions[idx], idx);

      questions[idx].evaluated_range = calificacionValue;
      questions[idx].feedback = feedbackValue;

      console.log(`[MODAL-EDITA-DEBUG] Valores resueltos pregunta ${idx + 1}:`, {
        evaluated_range: calificacionValue,
        feedback: feedbackValue,
        question_id: questions[idx].question_id
      });

      const req = {
        valid: "updateQuestion",
        id_log: questions[idx].id,
        question_id: questions[idx].question_id,
        user_id: questions[idx].user_id,
        evaluator_id: questions[idx].evaluator_id || emp.id_user,
        evaluated_range:
          calificacionValue === "" || calificacionValue === null
            ? ""
            : normalizeEvaluatedRangeForApi(calificacionValue),
        feedback:
          feedbackValue === "" || feedbackValue === null ? "" : feedbackValue,
        make_evaluation_id: emp.id_user,
      };

      console.log(`[MODAL-EDITA-DEBUG] Request final que se enviará:`, req);

      const maxRetries = 3;
      let success = false;

      for (let attempt = 1; attempt <= maxRetries && !success; attempt++) {
        try {
          console.log(`[MODAL-REQUEST] Intento ${attempt}/${maxRetries} para pregunta ${idx + 1}`);

          const url = API_HOST;
          const response = await httpApiPostText(
            url,
            "PUT",
            {
              "Content-Type": "application/json",
            },
            req
          );

          const parsed = parseApiResult(response);
          if (parsed.ok) {
            console.log(`[MODAL-SUCCESS] Pregunta ${idx + 1} guardada exitosamente`);
            success = true;
          } else {
            throw new Error(parsed.body?.message || response.message || "Respuesta no válida del servidor");
          }

        } catch (error) {
          console.error(`[MODAL-ERROR] Intento ${attempt} falló para pregunta ${idx + 1}:`, error);

          if (attempt === maxRetries) {
            console.error(`[MODAL-ERROR] FALLO CRÍTICO: No se pudo guardar pregunta ${idx + 1} tras ${maxRetries} intentos`);
            setMsgError(`No fue posible guardar la pregunta ${idx + 1}.`);
            setShowMsgError(true);
            return false;
          }

          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }

      console.log(`[MODAL-EDITA-DEBUG] === FIN GUARDAR PREGUNTA ${idx + 1} ===`);
      return success;
    };

    const persistAnswersProgressEdita = async () => {
      if (!questions?.length) return false;

      if (questions[carouselIndex]) {
        const current = resolveQuestionAnswer(questions[carouselIndex], carouselIndex);
        questions[carouselIndex].evaluated_range = current.evaluated_range;
        questions[carouselIndex].feedback = current.feedback;
      }

      const answers: any[] = [];
      for (let i = 0; i < questions.length; i++) {
        const { evaluated_range, feedback } = resolveQuestionAnswer(questions[i], i);
        questions[i].evaluated_range = evaluated_range;
        questions[i].feedback = feedback;
        const hasData =
          String(evaluated_range || "").trim() !== "" ||
          String(feedback || "").trim() !== "";
        if (!hasData) continue;
        answers.push({
          id_log: questions[i].id,
          question_id: questions[i].question_id,
          user_id: questions[i].user_id,
          evaluator_id: questions[i].evaluator_id || emp.id_user,
          evaluated_range: normalizeEvaluatedRangeForApi(evaluated_range),
          feedback,
          make_evaluation_id: emp.id_user,
        });
      }

      if (!answers.length) return true;

      const response = await httpApiPostText(
        API_HOST,
        "POST",
        { "Content-Type": "application/json" },
        {
          valid: "saveAnswersProgress",
          answers,
          evaluation_history_id: questions[0]?.evaluation_history_id || 0,
        }
      );
      const parsed = parseApiResult(response);
      if (!parsed.ok) {
        for (const a of answers) {
          const idx = questions.findIndex((q: any) => Number(q.id) === Number(a.id_log));
          if (idx < 0) continue;
          const ok = await guardarPregunta(idx);
          if (!ok) return false;
        }
      }
      return true;
    };

    const finalizarFase1Edicion = async () => {
      const nQuestionsF1 = questions.filter((q: any) =>
        Number(q.evaluation_phase) === 1 ||
        String(q.evaluation_phase ?? "").toLowerCase().includes("1")
      ).length;
      const fase1Answers: any[] = [];

      if (questions[carouselIndex]) {
        const current = resolveQuestionAnswer(questions[carouselIndex], carouselIndex);
        questions[carouselIndex].evaluated_range = current.evaluated_range;
        questions[carouselIndex].feedback = current.feedback;
      }

      const totalF1 = nQuestionsF1 > 0 ? Math.min(nQuestionsF1, questions.length) : questions.length;

      for (let i = 0; i < totalF1; i++) {
        const question = questions[i];
        const { evaluated_range: evaluatedRange, feedback } = resolveQuestionAnswer(question, i);
        question.evaluated_range = evaluatedRange;
        question.feedback = feedback;

        fase1Answers.push({
          id_log: question.id,
          question_id: question.question_id,
          user_id: question.user_id,
          evaluator_id: question.evaluator_id || emp.id_user,
          evaluated_range: normalizeEvaluatedRangeForApi(evaluatedRange),
          feedback,
          make_evaluation_id: emp.id_user,
        });
      }

      try {
        const req = {
          valid: "saveFase1Complete",
          answers: fase1Answers,
          evaluation_history_id: questions[0]?.evaluation_history_id || 0,
        };

        const response = await httpApiPostText(
          API_HOST,
          "POST",
          { "Content-Type": "application/json" },
          req
        );
        const parsed = parseApiResult(response);

        if (!parsed.ok) {
          setMsgError(parsed.body?.message || "Error al finalizar la Fase 1");
          setShowMsgError(true);
          return false;
        }

        setFase1Saved(true);
        setIsFase1Complete(true);
        cerrarModal();
        return true;
      } catch (error) {
        setMsgError("Error al finalizar la Fase 1");
        setShowMsgError(true);
        return false;
      }
    };

    const OnClickGuardarSalir = async () => {
      if (!questions?.length) return;
      const ok = await persistAnswersProgressEdita();
      if (!ok) {
        setMsgError("No fue posible guardar el progreso de la evaluación.");
        setShowMsgError(true);
        return;
      }
      cerrarModal();
    };

    const finalizaEvaluacion = async () => {
      // ===========================================Inicio código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      // Preparar datos completos para console.log antes de finalizar
      const evaluacionCompleta = [];
      // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      // Itera sobre todas las preguntas y guarda cada una
      for (let i = 0; i < questions.length; i++) {
        const { evaluated_range, feedback } = resolveQuestionAnswer(questions[i], i);

        questions[i].evaluated_range = evaluated_range;
        questions[i].feedback = feedback;

        // ===========================================Inicio código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

        // Agregar al array completo para el log
        evaluacionCompleta.push({
          pregunta: i + 1,
          fase: i < nQuestionsF1 ? "Fase 1" : "Fase 2",
          question_id: questions[i].question_id,
          categoria: questions[i].categorie,
          titulo: questions[i].title,
          evaluated_range,
          feedback,
          minimum: questions[i].minimum,
          maximum: questions[i].maximum
        });

        // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

        console.log(`  - Después: evaluated_range="${questions[i].evaluated_range}", feedback="${questions[i].feedback}"`);
        console.log(`[MODAL-EDITA] Guardando pregunta ${i + 1}...`);

        const questionSaved = await guardarPregunta(i);
        if (!questionSaved) {
          return;
        }
      }

      // Envío del evaluador que estaba asignado al crear la evaluación (fallback al usuario actual)
      const collaboratorForFinish = data.find((d: any) => String(d.id_collaborator) === String(props.idColaborador));
      const assignedEvaluatorForFinish = collaboratorForFinish?.id_evaluator || emp.id_user;
      const req = {
        valid: "updateDateEvaluation",
        user_id: `${props.idColaborador}`,
        evaluator_id: `${assignedEvaluatorForFinish}`,
        date: `${fecha ? fecha.substring(0, 10) : ""}`,
      };

      // ===========================================Inicio código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      // 🔍 LOG 3: Datos finales enviados al servicio para guardar en BD (Modal Edición)
      console.log("=== FINALIZANDO EVALUACIÓN COMPLETA - DATOS ENVIADOS AL SERVICIO (MODAL EDICIÓN) ===", {
        servicio_endpoint: "updateDateEvaluation",
        request_data: req,
        colaborador_id: props.idColaborador,
        evaluator_id: emp.id_user,
        fecha: fecha ? fecha.substring(0, 10) : "",
        total_preguntas: evaluacionCompleta.length,
        evaluacion_completa: evaluacionCompleta,
        resumen_fases: {
          fase1_preguntas: evaluacionCompleta.filter(p => p.fase === "Fase 1").length,
          fase2_preguntas: evaluacionCompleta.filter(p => p.fase === "Fase 2").length
        }
      });

      // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      const url = API_HOST;
      const response = await httpApiPostText(
        url,
        "PUT",
        {
          "Content-Type": "application/json",
        },
        req
      );

      if (response.code < 200 || response.code >= 300) {
        setMsgError("No fue posible finalizar la evaluación.");
        setShowMsgError(true);
        return;
      }

      setShowFinalizalQuestion(false);
      clearResumeQuestionLocal();
      cerrarModal();
    };

    const OnClickFinalizarSalir = async () => {
      const isLastQuestion = carouselIndex === questions.length - 1;

      if (isLastQuestion && isFase2Complete) {
        setShowFinalizalQuestion(true);
        return;
      }

      const fase1CompletaDesdeBD = detectarEstadoFasesDesdeDB();

      if (!fase1Saved && !fase1CompletaDesdeBD) {
        await finalizarFase1Edicion();
        return;
      }

      // Actualizar valores actuales del DOM antes de validar
      for (let i = 0; i < questions.length; i++) {
        const nmSel = `quailify-${questions[i].question_id}-${i}`;
        const nmTxt = `qst-${questions[i].question_id}-${i}`;
        questions[i].evaluated_range = getElement(nmSel);
        questions[i].feedback = getElement(nmTxt);
      }
      // ===========================================Inicio código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      // Preparar datos para console.log de Fase 2
      const nQuestionsF1 = questions.filter((q: any) => q.evaluation_phase === 1).length;
      const fase2Data = [];
      for (let i = nQuestionsF1; i < questions.length; i++) {
        fase2Data.push({
          pregunta: i + 1,
          question_id: questions[i].question_id,
          evaluated_range: questions[i].evaluated_range,
          feedback: questions[i].feedback,
          user_id: questions[i].user_id,
          evaluator_id: questions[i].evaluator_id
        });
      }

      // 🔍 LOG 2: Datos guardados de Fase 2
      console.log("=== FASE 2 COMPLETADA - DATOS GUARDADOS ===", {
        colaborador_id: props.idColaborador,
        evaluator_id: emp.id_user,
        fecha: fecha ? fecha.substring(0, 10) : "",
        total_preguntas_fase2: fase2Data.length,
        datos_fase2: fase2Data
      });
      // ===========================================Fín código que se debe quitar, solo esta para rastrear la data que se envía al backend===========================================

      // Validar TODAS las preguntas (Fase 1 y Fase 2)
      const preguntasIncompletas = [];

      for (let i = 0; i < questions.length; i++) {
        const pregunta = questions[i];
        const esPreguntaCalificable = pregunta.minimum < pregunta.maximum;
        const fase = i < nQuestionsF1 ? "Fase 1" : "Fase 2";

        if (esPreguntaCalificable) {
          // Verificar si tiene calificación válida (no vacío, no null, no "0", no "0.0", no "0.00")
          const valorCalificacion =
            pregunta.evaluated_range?.toString().trim() || "";
          const esCalificacionValida =
            valorCalificacion !== "" &&
            valorCalificacion !== "0" &&
            valorCalificacion !== "0.0" &&
            valorCalificacion !== "0.00" &&
            valorCalificacion !== "0,0" &&
            valorCalificacion !== "0,00" &&
            valorCalificacion !== "0.000000" &&
            parseFloat(valorCalificacion.replace(",", ".")) >= 1;

          if (!esCalificacionValida) {
            preguntasIncompletas.push(
              `${fase} - Pregunta ${i + 1
              }: Falta calificación válida (debe ser mayor a 0)`
            );
          }
        }
      }
      if (preguntasIncompletas.length > 0) {
        setMsgError(
          `Para finalizar la evaluación, debe completar todos los campos:\n\n${preguntasIncompletas.join(
            "\n"
          )}`
        );
        setShowMsgError(true);
        return;
      }

      guardarPregunta(carouselIndex);
      setShowFinalizalQuestion(true);
    };

    const getElement = (id: string) => {
      const input = document.getElementById(id) as HTMLInputElement | null;

      if (input != null && input.parentNode) {
        return input.value;
      }

      console.warn(`[MODAL-WARN] Elemento no encontrado o desconectado: ${id}`);
      return null;
    };

    const setElementEdit = (id: string, value: string) => {
      const inputEdit = document.getElementById(id) as HTMLInputElement | null;

      if (inputEdit != null) {
        inputEdit.value = value;
      }
    };

    const ControlledCarousel = (props: any) => {
      //const [index, setIndex] = useState(nQuestionsF1ini);
      const { preguntas, index, onIndexChange: setIndex } = props;
      const [showMsgErrorCalificacion, setShowMsgErrorCalificacion] =
        useState(false);
      let [forms, setForms] = useState({});

      // Debounce para evitar múltiples guardados rápidos
      let timeoutGuardado: NodeJS.Timeout | null = null;

      const guardarConDebounce = (idx: number) => {
        if (timeoutGuardado) {
          clearTimeout(timeoutGuardado);
        }

        timeoutGuardado = setTimeout(() => {
          console.log(`[MODAL-DEBOUNCE] Ejecutando guardado de pregunta ${idx + 1} tras pausa`);
          guardarPregunta(idx);
        }, 300); // Esperar 300ms de inactividad
      };

      const guardarPregunta = async (idx: number) => {
        try {
          const req = {
            valid: "updateQuestion",
            id_log: questions[idx].id,
            question_id: questions[idx].question_id,
            user_id: questions[idx].user_id,
            evaluator_id: questions[idx].evaluator_id,
            evaluated_range:
              questions[idx].evaluated_range === "" ||
                questions[idx].evaluated_range === null
                ? ""
                : normalizeEvaluatedRangeForApi(questions[idx].evaluated_range),
            feedback:
              questions[idx].feedback === "" || questions[idx].feedback === null
                ? ""
                : questions[idx].feedback,
            make_evaluation_id: emp.id_user,
          };
          const url = API_HOST;
          const response = await httpApiPostText(
            url,
            "PUT",
            {
              "Content-Type": "application/json",
            },
            req
          );

          return response.code >= 200 && response.code < 300;
        } catch (error) {
          return false;
        }
      };

      // ModalRealizaEvaluacion - ControlledCarousel2 (Modal 2)

      const carousellSelect = (selectedIndex: number, e: any) => {

        setShowMsgErrorCalificacion(false);
        let idxArr = index;
        indiceArr = selectedIndex;
        setIndex(selectedIndex);

        const nmTxt = `qst-${props.preguntas[idxArr].question_id}-${idxArr}`;
        const { evaluated_range, feedback } = resolveQuestionAnswer(
          questions[idxArr],
          idxArr
        );

        questions[idxArr].feedback = feedback;
        questions[idxArr].evaluated_range = evaluated_range;

        forms = { ...forms, [nmTxt]: feedback };
        setForms({ ...forms });

        console.log(`[MODAL-EDITA] Programando guardado de pregunta ${idxArr + 1}...`);
        guardarConDebounce(idxArr);

        // Verificar si las fases están completas cada vez que se cambia de pregunta
        setTimeout(() => {
          checkFasesCompleteness();
        }, 300);
      };

      const onSelectorBlur = (evnt: any) => {
        const valSelector = parseFloat(evnt.target.value);
        const minSelector = parseFloat(evnt.target.min);
        const maxSelector = parseFloat(evnt.target.max);

        if (evnt.target.value === "") {
          // no hacer nada, por ahora
          setShowMsgErrorCalificacion(false);
        } else if (valSelector < minSelector || valSelector > maxSelector) {
          setShowMsgErrorCalificacion(true);
          const nmSlt = evnt.target.id;
          setElementEdit(nmSlt, "");
        }
      };

      useEffect(() => {
        const updateQuestions = () => {
          if (props.preguntas.length > 0) {
            // proceso de actualizar los elementos de las preguntas con lo que está guardado en BD
            props.preguntas.map((qst: any, idx: number) => {
              const nmSel = `quailify-${qst.question_id}-${idx}`;
              const nmTxt = `qst-${qst.question_id}-${idx}`;

              let evr: string = "0.0";
              if (qst.evaluated_range !== null && qst.evaluated_range !== "") {
                evr = qst.evaluated_range;
                setElementEdit(nmSel, formatDisplayValue(evr));
              }
              setElementEdit(nmTxt, qst.feedback || "");
            });
          }
        };

        updateQuestions();
      }, []);

      const habilitarBtnUltimaPregunta = (value: string, index: number): void => {
        const isLastQuestion = index === questions.length - 1;
        const cleanValue = value.replace(",", ".").trim();
        const numericValue = parseFloat(cleanValue);
        const isValidValue = !isNaN(numericValue) && numericValue > 0;
        setTimeout(() => {
          if (isLastQuestion && isValidValue) {
            checkFasesCompleteness();
          }
        }, 1500);
      }

      useEffect(() => {
        const applyIndicatorClasses = () => {
          const container = document.getElementById(
            `carousel-edita-${props.idColaborador}`
          );
          if (!container) return false;
          const indicators = container.querySelectorAll(
            ".carousel-indicators [data-bs-slide-to], .carousel-indicators [data-bs-target]"
          );
          if (indicators.length === 0) return false;

          const isPhase1 = (q: any) => {
            const phaseRaw = String(q?.evaluation_phase ?? "")
              .trim()
              .toLowerCase();
            const phaseNum = Number(phaseRaw);
            if (q?.evaluation_phase === 1 || phaseNum === 1) return true;
            if (phaseRaw.includes("fase 1") || phaseRaw.includes("fase1"))
              return true;
            return false;
          };

          const isPhase2 = (q: any) => {
            const phaseRaw = String(q?.evaluation_phase ?? "")
              .trim()
              .toLowerCase();
            const phaseNum = Number(phaseRaw);
            if (q?.evaluation_phase === 2 || phaseNum === 2) return true;
            if (phaseRaw.includes("fase 2") || phaseRaw.includes("fase2"))
              return true;
            if (phaseRaw.includes("retroaliment")) return true;
            return false;
          };

          const allPhasesUndefined = props.preguntas.every(
            (q: any) =>
              q?.evaluation_phase === undefined ||
              q?.evaluation_phase === null ||
              q?.evaluation_phase === ""
          );

          const firstPhase2Index = props.preguntas.findIndex((q: any) =>
            isPhase2(q)
          );
          let nQuestionsF1 = props.preguntas.filter((q: any) =>
            isPhase1(q)
          ).length;
          if (firstPhase2Index !== -1) {
            nQuestionsF1 = firstPhase2Index;
          }
          if (nQuestionsF1 <= 0 && props.preguntas.length > 0) {
            nQuestionsF1 = Math.floor(props.preguntas.length / 2);
          }
          const forceIndexSplit = allPhasesUndefined;

          const getPhaseClass = (
            q: any,
            idx: number,
            fallbackF1Count: number
          ) => {
            if (forceIndexSplit) {
              return idx < fallbackF1Count ? "phase-1" : "phase-2";
            }
            if (isPhase2(q)) return "phase-2";
            if (isPhase1(q)) return "phase-1";
            return idx < fallbackF1Count ? "phase-1" : "phase-2";
          };

          indicators.forEach((indicator, idx) => {
            indicator.classList.remove("phase-1", "phase-2");
            const q = props.preguntas[idx];
            const phaseClass = getPhaseClass(q, idx, nQuestionsF1);
            indicator.classList.add(phaseClass);
          });
          return true;
        };

        if (props.preguntas.length === 0) {
          return;
        }

        const container = document.getElementById(
          `carousel-edita-${props.idColaborador}`
        );
        if (!container) {
          return;
        }

        const observer = new MutationObserver(() => {
          applyIndicatorClasses();
        });

        observer.observe(container, {
          childList: true,
          subtree: true,
        });

        const timeoutId = setTimeout(() => {
          applyIndicatorClasses();
        }, 60);

        return () => {
          clearTimeout(timeoutId);
          observer.disconnect();
        };
      }, [props.preguntas, props.idColaborador]);

      return (
        <div className="eval-carousel-wrapper">
          <Carousel
            id={`carousel-edita-${props.idColaborador}`}
            className="custom-carousel"
            activeIndex={index}
            onSelect={carousellSelect}
            interval={null}
            wrap={false}
            indicators={false}
            prevIcon={
              <span
                className="carousel-control-prev-icon"
                aria-hidden="true"
              ></span>
            }
            nextIcon={
              <span
                className="carousel-control-next-icon"
                aria-hidden="true"
              ></span>
            }
          >
            {props.preguntas.map((obj: any, idx: number) => {
              const phase = String(obj.evaluation_phase ?? '').trim();
              const isP1 = phase === '1' || phase === 'Fase 1' || phase === 'fase 1' || obj.evaluation_phase === 1;
              const phaseClass = isP1 ? 'phase-1' : 'phase-2';
              const phaseLabel = isP1 ? 'Fase 1' : 'Fase 2: Retroalimentación';
              return (
                <Carousel.Item key={obj.id}>
                  <div className={`eval-question-card ${phaseClass}`}>
                    <div className="eval-card-header">
                      <span className={`eval-phase-badge ${phaseClass}`}>{phaseLabel}</span>
                      <span className="eval-category">{obj.categorie}</span>
                    </div>
                    <h5 className="eval-title">{obj.title || obj.categorie}</h5>
                    {obj.description && obj.description !== obj.title && <p className="eval-description">{obj.description}</p>}
                    {isRateableQuestion(obj) ? (
                      <div className="eval-score-section">
                        <label className="eval-score-label">
                          Calificación <span className="eval-score-range">({obj.minimum} – {obj.maximum})</span>
                        </label>
                        <div className="numeric-input-container">
                          <input
                            type="text"
                            id={`quailify-${obj.question_id}-${idx}`}
                            placeholder=""
                            className="form-control p-2 numeric-input"
                            onKeyPress={(e) => {
                              const regex = /[0-9,\.]/;
                              if (
                                !regex.test(e.key) &&
                                e.key !== "Backspace" &&
                                e.key !== "Tab"
                              ) {
                                e.preventDefault();
                              }
                              if (
                                (e.key === "," || e.key === ".") &&
                                (e.currentTarget.value.includes(",") ||
                                  e.currentTarget.value.includes("."))
                              ) {
                                e.preventDefault();
                              }
                              if (
                                (e.key === "," || e.key === ".") &&
                                e.currentTarget.value === ""
                              ) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              habilitarBtnUltimaPregunta(e.target.value, index)
                              let newValue = e.target.value.replace(/\./g, ",");
                              const numericValue = parseFloat(
                                newValue.replace(",", ".")
                              );
                              if (
                                !isNaN(numericValue) &&
                                numericValue > obj.maximum
                              ) {
                                newValue = obj.maximum
                                  .toString()
                                  .replace(".", ",");
                              }
                              e.target.value = newValue;
                              if (questions[idx]) {
                                questions[idx].evaluated_range = newValue;
                              }
                            }}
                              onBlur={(e) => {
                              onBlurLogic(e, obj, idx, questions, setShowMsgErrorCalificacion);
                              if (questions[idx] && e.target.value) {
                                questions[idx].evaluated_range = e.target.value;
                              }
                              setTimeout(() => checkFasesCompleteness(), 50);
                            }}
                            onFocus={() => setShowMsgErrorCalificacion(false)}
                          />
                          <div className="numeric-input-arrows">
                            <button
                              type="button"
                              className="arrow-button up"
                              onClick={() => {
                                const input = document.getElementById(
                                  `quailify-${obj.question_id}-${idx}`
                                ) as HTMLInputElement;
                                let value = input.value.replace(/\./g, ",");
                                let numValue = parseFloat(
                                  value.replace(",", ".") || "0"
                                );
                                if (value === "") numValue = obj.minimum;
                                numValue = Math.min(numValue + 0.1, obj.maximum);
                                input.value = numValue.toFixed(1).replace(".", ",");
                                if (questions[idx]) {
                                  questions[idx].evaluated_range = input.value;
                                }
                                setShowMsgErrorCalificacion(false);
                              }}
                              tabIndex={-1}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-up" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="arrow-button down"
                              onClick={() => {
                                const input = document.getElementById(
                                  `quailify-${obj.question_id}-${idx}`
                                ) as HTMLInputElement;
                                let value = input.value.replace(/\./g, ",");
                                let numValue = parseFloat(
                                  value.replace(",", ".") || "0"
                                );
                                if (value === "") numValue = obj.maximum;
                                numValue = Math.max(numValue - 0.1, obj.minimum);
                                input.value = numValue.toFixed(1).replace(".", ",");
                                setShowMsgErrorCalificacion(false);
                              }}
                              tabIndex={-1}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-chevron-down" viewBox="0 0 16 16">
                                <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <Alert variant="danger" show={showMsgErrorCalificacion}>
                          <FaRegHandPaper /> Valor de la calificación fuera de los valores permitidos. Este valor no será guardado.
                        </Alert>
                      </div>
                    ) : null}
                    <div className="eval-obs-section">
                      <label className="eval-obs-label">Observaciones</label>
                      <textarea
                        id={`qst-${obj.question_id}-${idx}`}
                        placeholder="Escriba sus observaciones aquí..."
                        className="eval-obs-textarea"
                        defaultValue={obj.feedback || ""}
                        onChange={(e) => {
                          console.log(`[MODAL-FEEDBACK] Actualizando feedback pregunta ${idx + 1}: "${e.target.value}"`);
                        }}
                        onBlur={(e) => {
                          if (questions[idx]) {
                            questions[idx].feedback = e.target.value;
                          }
                          setTimeout(() => checkFasesCompleteness(), 50);
                        }}
                      />
                    </div>
                  </div>
                </Carousel.Item>
              );
            })}
          </Carousel>
          {(() => {
            const navGroups: { label: string; indices: number[] }[] = [];
            props.preguntas.forEach((q: any, qIdx: number) => {
              const phase = String(q.evaluation_phase ?? '').trim();
              const isP1 = phase === '1' || phase === 'Fase 1' || phase === 'fase 1' || q.evaluation_phase === 1;
              const label = isP1 ? 'Fase 1' : 'Fase 2';
              let grp = navGroups.find(g => g.label === label);
              if (!grp) { grp = { label, indices: [] }; navGroups.push(grp); }
              grp.indices.push(qIdx);
            });
            return (
              <div className="eval-phase-nav">
                {navGroups.map((grp) => (
                  <div key={grp.label} className="eval-phase-group">
                    <span className={`eval-phase-group-label ${grp.label === 'Fase 1' ? 'phase-1-label' : 'phase-2-label'}`}>{grp.label}</span>
                    <div className="eval-nav-buttons">
                      {grp.indices.map((globalIdx, localIdx) => {
                        const q = props.preguntas[globalIdx];
                        const domVal = getElement(`quailify-${q.question_id}-${globalIdx}`);
                        const ratingVal = domVal !== null ? domVal.trim() : (q.evaluated_range?.toString() || '').trim();
                        const numericRating = parseFloat(ratingVal.replace(',', '.'));
                        const isAnswered = isRateableQuestion(q)
                          ? (ratingVal !== '' && !isNaN(numericRating) && numericRating >= 1)
                          : true;
                        const isCurrent = index === globalIdx;
                        const btnPhase = grp.label === 'Fase 1' ? 'phase-1-btn' : 'phase-2-btn';
                        return (
                          <button
                            key={globalIdx}
                            className={`eval-nav-btn ${btnPhase} ${isCurrent ? 'active' : isAnswered ? 'answered' : ''}`}
                            onClick={() => carousellSelect(globalIdx, null)}
                          >
                            {localIdx + 1}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      );
    };

    return (
      <>
        <Modal
          show={props.Show}
          centered={true}
          size="xl"
          onHide={props.handlerClickEnd}
          backdrop="static"
          keyboard={true}
        >
          <Modal.Header closeButton>
            <Modal.Title className="h3 text-center">
              Evaluación de: <span className="highlight">{props.name}</span>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body
            style={{
              height: "480px",
              overflow: "hidden",
            }}
          >
            <div className="w-100">
              {comenzar ? (
                <div
                  className="d-flex align-items-center justify-content-center w-100"
                  style={{ height: "440px" }}
                >
                  <div className="d-flex flex-column gap-3">
                    <Button
                      type="button"
                      className="btn btn-success"
                      style={{ width: '100px', height: '40px' }}
                      onClick={HandlerClickComenzar}
                    >
                      Comenzar
                    </Button>
                    <Button
                      type="button"
                      className="btn btn-secondary"
                      style={{ width: '100px', height: '40px' }}
                      onClick={props.HandlerClickOk}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="d-flex justify-content-center w-100"
                  style={{ paddingBottom: "50px" }}
                >
                  <ControlledCarousel
                    idColaborador={props.idColaborador}
                    preguntas={questions}
                    index={carouselIndex}
                    onIndexChange={setCarouselIndex}
                  />
                </div>
              )}
            </div>
          </Modal.Body>

          <Modal.Footer className="">
            {!comenzar && (
              <>
                <div className="d-flex justify-content-center w-100 gap-2">
                  <Button
                    type="button"
                    className="btn btn-outline-secondary"
                    style={{ padding: "8px 24px", fontWeight: "bold" }}
                    onClick={OnClickGuardarSalir}
                    disabled={!questions?.length}
                    title="Guardar la evaluación para continuar luego"
                  >
                    Guardar
                  </Button>
                  <Button
                    type="button"
                    className="btn"
                    style={{
                      backgroundColor: (fase1Saved ? isFase2Complete : isFase1Complete)
                        ? "#28a745"
                        : "#6c757d",
                      color: "white",
                      border: "none",
                      padding: "8px 24px",
                      fontWeight: "bold"
                    }}
                    onClick={OnClickFinalizarSalir}
                    disabled={!(fase1Saved ? isFase2Complete : isFase1Complete)}
                    title={
                      !(fase1Saved ? isFase2Complete : isFase1Complete)
                        ? (fase1Saved ? "Complete todas las calificaciones de Fase 2 para habilitar" : "Complete todas las calificaciones de Fase 1 para habilitar")
                        : ""
                    }
                  >
                    {(() => {
                      const isLastQuestion = carouselIndex === questions.length - 1;
                      if (isLastQuestion && isFase2Complete) {
                        return "Finalizar Evaluación";
                      }
                      return fase1Saved ? "Finalizar Fase 2" : "Finalizar Fase 1";
                    })()}
                  </Button>
                </div>

                <div className="text-center mt-2 w-100">
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    {(() => {
                      const isLastQuestion = carouselIndex === questions.length - 1;
                      if (isLastQuestion && isFase2Complete) {
                        return 'Fase 1 y Fase 2 completadas - Use "Finalizar Evaluación" para terminar';
                      }
                      return !fase1Saved
                        ? 'Complete la Fase 1 y use "Finalizar Fase 1" para dejar la evaluación en estado "Continuar"'
                        : 'Fase 1 completada - Complete Fase 2 y use "Finalizar Fase 2" para terminar la Evaluación';
                    })()}
                  </small>
                </div>
              </>
            )}
          </Modal.Footer>
        </Modal>
        {/* Cuadro de dialogo de finalización de EVALUACIÓN*/}
        <MsgDialog
          Show={showFinalizalQuestion}
          Title="Finalizar Evaluación"
          Message={"Una vez finalizada no podrá modificar la información"}
          BtnOkName="Finalizar"
          BtnNokName="Cancelar"
          HanlerdClickNok={() => setShowFinalizalQuestion(false)}
          HandlerClickOk={finalizaEvaluacion}
        />
      </>
    );
  };

  const onBlurLogic = (
    e: any,
    obj: { minimum: number; maximum: number },
    idx: number,
    questions: any[],
    setShowMsgErrorCalificacion: (val: boolean) => void
  ) => {
    let value = e.target.value.replace(/\./g, ",");
    const numValue = parseFloat(value.replace(",", "."));

    if (value === "") {
      setShowMsgErrorCalificacion(false);
    } else if (
      isNaN(numValue) ||
      numValue < obj.minimum ||
      numValue > obj.maximum
    ) {
      setShowMsgErrorCalificacion(true);
      e.target.value = "";
    } else {
      // ✅ SOLUCIÓN: APLICAR FORMATEO AUTOMÁTICO CUANDO EL VALOR ES VÁLIDO
      // Formatear el valor a 1 decimal con coma como separador
      const valorFormateado = numValue.toFixed(1).replace(".", ",");
      e.target.value = valorFormateado;

      console.log(`[FORMATEO-AUTO] Valor original: "${value}" → Valor formateado: "${valorFormateado}"`);

      setShowMsgErrorCalificacion(false);

      if (questions[idx]) {
        questions[idx].evaluated_range = e.target.value;
      }
    }
  }


  const formatDisplayValue = (value: string): string => {
    if (!value || value === "null" || value === "") return "";

    const numValue = parseFloat(value.replace(",", "."));
    if (isNaN(numValue)) return value;

    return numValue.toLocaleString('es-ES', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };
  return (
    <div className="container bg-white page-shell">
      <BarraMenu />
      <div className="page-shell__body">
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <PageHero
              icon={<FaInfoCircle />}
              title="¿No encuentra su colaborador?"
              subtitle="Solicite a su líder que le asigne la Evaluación"
              className="h-100"
            />
          </div>

          <div className="col-md-8">
            <PageHero
              icon={<FaClipboardCheck />}
              title="Panel de Evaluaciones"
              subtitle="Gestión de evaluaciones de desempeño"
              stats={
                <div className="dashboard-hero__stats">
                  <span className="dashboard-hero__stat dashboard-hero__stat--pending">
                    <FaClock />{" "}
                    {data.filter((item) => !item.evaluation_history_date_close).length} Pendientes
                  </span>
                  <span className="dashboard-hero__stat dashboard-hero__stat--done">
                    <FaCheckCircle />{" "}
                    {data.filter((item) => item.evaluation_history_date_close).length} Completadas
                  </span>
                  <span className="dashboard-hero__stat dashboard-hero__stat--total">
                    <FaUsers /> {data.length} Total
                  </span>
                </div>
              }
            />
          </div>
        </div>

          <div className="row shadow rounded mt-3 mb-3">
            <div
              className="search-container"
              style={{
                padding: "15px 15px 5px",
                backgroundColor: "#f8f9fa",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
              }}
            >
              {/* Búsqueda server-side con debounce — mín. 3 caracteres */}
              <div style={{ position: "relative", marginBottom: "12px" }}>
                <input
                  type="text"
                  placeholder="Buscar colaborador (mín. 3 caracteres)..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="form-control search-input"
                  style={{ borderRadius: "20px", paddingLeft: "15px", transition: "all 0.3s" }}
                />
                {searchInput.length > 0 && searchInput.trim().length < SEARCH_MIN_CHARS && (
                  <small className="text-muted" style={{ position: "absolute", bottom: "-17px", left: "14px", fontSize: "11px" }}>
                    Escribe al menos {SEARCH_MIN_CHARS} caracteres para buscar
                  </small>
                )}
              </div>

              {/* Filtros client-side (actúan sobre la página actual) */}
              <div className="d-flex gap-2 flex-wrap mt-2 mb-2">
                {isDigitalAnnualPanel && (
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Filtrar por cliente..."
                  value={filtroCliente}
                  onChange={e => { setFiltroCliente(e.target.value); filtroClienteRef.current = e.target.value; }}
                  style={{ borderRadius: "20px", paddingLeft: "15px", maxWidth: "220px" }}
                /> )}
                <select
                  className="form-select"
                  style={{ maxWidth: "200px" }}
                  value={filtroEstadoEval}
                  onChange={e => { setFiltroEstadoEval(e.target.value); filtroEstadoRef.current = e.target.value; }}
                >
                  <option value="">Todos los estados</option>
                  <option value="1">Finalizada</option>
                  <option value="0">Abierta</option>
                  <option value="null">No realizada</option>
                </select>
              </div>
            </div>
            <DataTable
              columns={columns}
              data={filteredData}
              selectableRowsHighlight
              fixedHeader
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              onChangePage={handlePageChange}
              paginationPerPage={PER_PAGE}
              paginationDefaultPage={currentPage}
              highlightOnHover
              paginationComponentOptions={pagOptions}
              progressPending={isLoading}
              progressComponent={
                <div className="py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </div>
              }
              noDataComponent={
                <div className="p-4">No hay evaluaciones para mostrar</div>
              }
              customStyles={{
                table: {
                  style: {
                    backgroundColor: "white",
                    borderRadius: "0 0 8px 8px",
                  },
                },
                headRow: {
                  style: {
                    backgroundColor: "#f0f2f5",
                    minHeight: "50px",
                    borderTop: "1px solid #dee2e6",
                    borderBottom: "2px solid #dee2e6",
                    fontWeight: "bold",
                    color: "#495057",
                  },
                },
                headCells: {
                  style: {
                    fontSize: "14px",
                    padding: "16px 8px",
                    textTransform: "uppercase",
                  },
                },
                rows: {
                  style: {
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#333",
                    minHeight: "55px",
                    "&:not(:last-of-type)": {
                      borderBottomStyle: "solid",
                      borderBottomWidth: "1px",
                      borderBottomColor: "#e9ecef",
                    },
                  },
                  highlightOnHoverStyle: {
                    backgroundColor: "#f8f9ff",
                    borderBottomColor: "#c7d5e3",
                    borderRadius: "4px",
                    transition: "all 0.3s",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  },
                },
                cells: {
                  style: {
                    paddingLeft: "16px",
                    paddingRight: "16px",
                  },
                },
                pagination: {
                  style: {
                    borderTop: "1px solid #dee2e6",
                    backgroundColor: "#f8f9fa",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "bold",
                    color: "#555",
                    padding: "16px",
                  },
                  pageButtonsStyle: {
                    borderRadius: "50%",
                    height: "32px",
                    width: "32px",
                    padding: "5px",
                    margin: "0px 5px",
                    transition: "0.4s",
                    backgroundColor: "transparent",
                    "&:hover:not(:disabled)": {
                      backgroundColor: "#007bff",
                      color: "white",
                    },
                  },
                },
              }}
            />
          </div>
      </div>
      {/* Cuadro de dialogo Realiazar nueva EVALUACIÓN desempeño */}
      <MsgDialog
        Show={showRealizaEvalQuestion}
        Title="Nueva Evaluación de Desempeño"
        Message={pregunta}
        BtnOkName="Si"
        BtnNokName="No"
        HanlerdClickNok={() => setShowRealizaEvalQuestion(false)}
        HandlerClickOk={formatoEvaluacion}
      />
      {/* Cuadro de dialogo edita EVALUACIÓN desempeño ya creada */}
      <MsgDialog
        Show={showEditaEvalQuestion}
        Title="Continuación de Evaluación desempeño"
        Message={pregunta}
        BtnOkName="Si"
        BtnNokName="No"
        HanlerdClickNok={() => setShowEditaEvalQuestion(false)}
        HandlerClickOk={formatoEdicionEvaluacion}
      />
      {/**************************
       ****Formato de edición de EVALUACIÓN
       ***************************/}
      <ModalEditaEvaluacion
        Show={showEditaEval}
        name={nombreUsr}
        idColaborador={idUsr}
        HandlerClickOk={cerrarModal}
        handlerClickEnd={cerrarModal}
      />
      {/**************************
       ****Formato de realización de EVALUACIÓN
       ***************************/}
      <ModalRealizaEvaluacion
        Show={showRealizaEval}
        name={nombreUsr}
        idColaborador={idUsr}
        fechaVinculacion={fechaVinculacion}
        HandlerClickOk={cerrarModalRealiza}
        handlerClickEnd={cerrarModalRealiza}
      />
      <MsgDialog
        Show={showResetConfirm}
        Title="Reiniciar Evaluación"
        Message="¿Está seguro que desea reiniciar esta evaluación? Se borrarán todas las respuestas guardadas."
        BtnOkName="Sí, reiniciar"
        BtnNokName="Cancelar"
        HandlerClickOk={() => resetHistoryId !== null && resetEvaluation(resetHistoryId)}
        HanlerdClickNok={() => setShowResetConfirm(false)}
      />
      <MsgDialog
        Show={showMsgError}
        Title="Evaluación desempeño"
        Message={msgError}
        BtnOkName="Aceptar"
        BtnNokName=""
        HanlerdClickNok={null}
        HandlerClickOk={() => setShowMsgError(false)}
      />
    </div>
  );
};

export default RealizarEvaluacionesPage;